/**
 * Students Management View
 * Allows Admin and RRHH to manage students and their enrollments.
 */
const StudentView = {
    currentSection: 'registered', // 'registered' | 'admissions' | 'in_progress' | 'rejected' | 'formations'
    inProgressSubSection: 'drafts', // 'drafts' | 'rejected'
    currentFilter: 'all',
    currentTypeFilter: 'all',
    searchTerm: '', // buscador de la lista (nombre, DNI, email, teléfono o usuario)
    admissionsList: [],
    currentPage: 1,
    rowsPerPage: 10,
    pendingFilter: null, // 'noAccess' | 'noEmail' desde la caja «Pendiente»
    currentYearStart: null, // curso lectivo que se está viendo (se inicializa en init())
    selectedStudents: new Set(), // ids marcados para promocionar/dar de baja en bloque

    // Curso lectivo de hoy PARA MATRICULACIÓN/PROMOCIÓN (Oct-Sep) — deliberadamente
    // distinto del Sep-Ago que usa el resto de la app (Programación, Nóminas,
    // Pre-Matrícula, stats del dashboard) para el calendario de clases. Septiembre
    // es mes de transición: aunque las clases del curso nuevo ya hayan empezado, a
    // efectos de matrícula/traslado ese curso sigue siendo "el actual" hasta
    // octubre — es cuando se hacen los traslados de septiembre y lo que hasta
    // entonces era "el siguiente" pasa a ser "el actual".
    todayAcademicYearStart() {
        const now = new Date();
        return now.getMonth() >= 9 ? now.getFullYear() : now.getFullYear() - 1;
    },

    // Curso al que pertenece un alumno concreto: el suyo propio si lo tiene, o el
    // curso actual si es un registro antiguo de antes de que existiera este campo
    // (mismo criterio "legacy → curso actual" que Pre-Matrícula).
    yearOf(student) {
        return typeof student.academicYearStart === 'number' ? student.academicYearStart : this.todayAcademicYearStart();
    },

    // Cursos a ofrecer en el selector: todos los que tengan al menos un alumno, más
    // el curso actual y el siguiente como ancla aunque estén vacíos todavía (el
    // siguiente especialmente, porque es donde caen los alumnos que se vayan
    // matriculando de ahora en adelante y puede no tener nadie aún).
    getAvailableYears() {
        const years = new Set([this.todayAcademicYearStart(), this.todayAcademicYearStart() + 1]);
        StudentService.getStudents().forEach(s => years.add(this.yearOf(s)));
        return [...years].sort((a, b) => b - a);
    },

    setAcademicYear(yearStart) {
        const y = parseInt(yearStart, 10);
        if (Number.isNaN(y) || !this.canBrowseYears()) return;
        this.currentYearStart = y;
        this.currentPage = 1;
        this.selectedStudents.clear();
        this.repaintFilters();
    },

    // Al cambiar curso, tipo o formación solo se actualizan las opciones (y contadores)
    // de los desplegables y la tabla: la barra, la búsqueda y la posición no se tocan.
    // Si la barra no está (otra sección o primera carga) se repinta la vista completa.
    repaintFilters() {
        const area = document.getElementById('content-area');
        if (!area) return;
        // Editar, promocionar o dar acceso desde la ficha del alumno: se repinta la ficha, no la lista.
        if (document.getElementById('student-profile-container') && typeof StudentProfileView !== 'undefined') { StudentProfileView.refresh(); return; }
        const fresh = document.createElement('div');
        SafeHTML.set(fresh, this.render());
        const card = document.getElementById('sv-list-card'), freshCard = fresh.querySelector('#sv-list-card');
        const ids = ['sv-year-select', 'sv-formation-select'];
        const pairs = ids.map(id => [document.getElementById(id), fresh.querySelector(`#${id}`)]);
        if (!card || !freshCard || pairs.some(([old, next]) => !old !== !next)) { SafeHTML.set(area, this.render()); return; }
        for (const [old, next] of pairs) {
            if (!old) continue;
            const value = next.value;
            old.replaceChildren(...next.childNodes);
            old.value = value;
        }
        card.replaceChildren(...freshCard.childNodes);
        // Botones de tipo, cifras, caja de la derecha y el curso del título.
        for (const selector of ['#sv-type-group', '#sl-stats', '#sl-aside', '#sl-eyebrow', '#sl-registered-count']) {
            const now = document.querySelector(selector), next = fresh.querySelector(selector);
            if (now && next) now.replaceWith(next);
        }
    },

    renderYearOptionsForForm(selectedYear) {
        return this.getAvailableYears().map(y =>
            `<option value="${y}" ${y === selectedYear ? 'selected' : ''}>Curso ${y} / ${y + 1}</option>`
        ).join('');
    },

    render() {
        if (this.currentYearStart === null) this.currentYearStart = this.todayAcademicYearStart();
        // Profesorado (sin admisiones ni gestión de Alumnado): solo el curso actual.
        if (!this.canBrowseYears()) this.currentYearStart = this.todayAcademicYearStart();
        // Sin permiso de admisiones solo existe la sección de alumnos registrados.
        if (this.currentSection !== 'registered' && !this.canSeeAdmissions()) this.currentSection = 'registered';

        const allStudents = StudentService.getStudents();
        const formations = FormationService.getFormations();

        // «En proceso» y «Rechazados» son ahora pasos de Admisiones (enlaces y rutas antiguas).
        if (this.currentSection === 'in_progress' || this.currentSection === 'rejected') {
            this.admissionStep = this.currentSection === 'rejected' || this.inProgressSubSection === 'rejected' ? 'rejected' : 'draft';
            this.inProgressSubSection = 'drafts';
            this.currentSection = 'admissions';
        }
        if (this.currentSection === 'admissions') {
            return this.renderAdmissionsView();
        }
        if (this.currentSection === 'formations') {
            return this.renderFormationsView();
        }

        // 0. Filtrar por curso lectivo — nuevos ingresos y alumnos ya matriculados
        //    no se mezclan: cada uno cae en su curso (el actual por defecto para
        //    los antiguos, ver yearOf()).
        const yearStudents = allStudents.filter(s => this.yearOf(s) === this.currentYearStart);

        // Profesorado (sin gestión ni admisiones): solo tipos y formaciones con alumnos suyos.
        const onlyOwn = !this.canBrowseYears();
        const typeCounts = FormationTypeService.getTypes().map(t => [t.id, t.pluralName])
            .map(([type, label]) => [type, label, yearStudents.filter(s => (s.formations || []).some(fid => formations.find(f => f.id === fid)?.type === type)).length])
            .filter(([type, , count]) => count > 0 || FormationTypeService.getType(type)?.active);
        if (onlyOwn && this.currentTypeFilter !== 'all' && !typeCounts.some(([type, , count]) => type === this.currentTypeFilter && count > 0)) {
            this.currentTypeFilter = 'all';
        }

        // 1. Filtrar formaciones por tipo
        const filteredFormations = this.currentTypeFilter === 'all'
            ? formations
            : formations.filter(f => f.type === this.currentTypeFilter);

        // 2. Filtrar estudiantes por tipo de formación seleccionada
        let typeFilteredStudents = yearStudents;
        if (this.currentTypeFilter !== 'all') {
            const allowedFormationIds = filteredFormations.map(f => f.id);
            typeFilteredStudents = yearStudents.filter(s =>
                (s.formations || []).some(fid => allowedFormationIds.includes(fid))
            );
        }

        // 3. Resetear filtro específico si ya no pertenece a la categoría filtrada
        if (this.currentFilter !== 'all' && !filteredFormations.some(f => f.id === this.currentFilter)) {
            this.currentFilter = 'all';
        }
        if (onlyOwn && this.currentFilter !== 'all' && !typeFilteredStudents.some(s => (s.formations || []).includes(this.currentFilter))) {
            this.currentFilter = 'all'; // su pestaña está oculta
        }

        // 4. Filtrar estudiantes según la pestaña de formación específica activa
        const tabStudents = this.currentFilter === 'all'
            ? typeFilteredStudents
            : typeFilteredStudents.filter(s => (s.formations || []).includes(this.currentFilter));

        // El buscador repinta solo la tarjeta de la tabla con esta misma lista (ver setSearch).
        this._listContext = { tabStudents, formations };

        // Curso lectivo: separa a los alumnos ya matriculados de los nuevos ingresos,
        // que caen por defecto en el curso siguiente (ver StudentService.createStudent).
        // Primer filtro de la barra de filtros.
        // Profesorado: etiqueta fija con el curso actual en lugar del selector.
        const yearSelectorHtml = onlyOwn
            ? `<span class="program-year-badge is-static" title="Curso lectivo">CURSO ${this.currentYearStart} / ${this.currentYearStart + 1}</span>`
            : `
            <select id="sv-year-select" class="program-year-badge" ${UIEvents.attr("change", function(event,captured){StudentView.setAcademicYear(this.value);}, [])} title="Curso lectivo">
                ${this.getAvailableYears().map(y => `<option value="${y}" ${y === this.currentYearStart ? 'selected' : ''}>CURSO ${y} / ${y + 1}${y === this.todayAcademicYearStart() + 1 ? ' · PRÓXIMO' : ''}</option>`).join('')}
            </select>
        `;

        // Formation choices grouped by type, with their counts. Staff also see empty ones
        // (greyed); professors only their own formations with students.
        const formationCount = id => typeFilteredStudents.filter(s => (s.formations || []).includes(id)).length;
        const typeGroups = FormationTypeService.getTypes().map(type => [type, filteredFormations.filter(f => f.type === type.id)])
            .concat([[{ pluralName: 'Otras' }, filteredFormations.filter(f => !FormationTypeService.getType(f.type))]])
            .map(([type, list]) => [type, list.filter(f => !onlyOwn || formationCount(f.id) > 0)]).filter(([, list]) => list.length);
        const option = (value, label, count, selected) => `<option value="${Utils.sanitizeHTML(value)}" ${selected ? 'selected' : ''} ${count === 0 ? 'class="is-empty"' : ''}>${Utils.sanitizeHTML(label)} (${count})</option>`;
        // Tipo de formación en botones: «Todos» y cada tipo con su número.
        const typeButton = (value, label, count) => {
            const on = this.currentTypeFilter === value;
            return `<button type="button" class="sl-type ${on ? 'is-on' : ''} ${count === 0 ? 'is-empty' : ''}" data-type="${Utils.sanitizeHTML(value)}" aria-pressed="${on}" ${UIEvents.attr("click", function(event,captured){StudentView.switchTypeTab(String(captured[0]));}, [value])}>${Utils.sanitizeHTML(label)} · ${count}</button>`;
        };

        return `
            <div class="view-container dnr-proposal sv-view sl">
                ${this.renderSectionTabs(allStudents.length)}
                ${onlyOwn ? '' : `<div class="sl-stats" id="sl-stats">${this.renderStats(yearStudents, formations)}</div>`}

                <!-- Curso, tipo, formación y búsqueda en una sola barra -->
                <div class="sv-type-filter sv-toolbar sl-toolbar">
                    ${yearSelectorHtml}
                    <div class="sl-types" role="group" aria-label="Tipo de formación" id="sv-type-group">
                        ${typeButton('all', 'Todos', yearStudents.length)}
                        ${typeCounts.filter(([, , count]) => !onlyOwn || count > 0).map(([type, label, count]) => typeButton(type, label, count)).join('')}
                    </div>
                    <select id="sv-formation-select" class="sv-filter-select is-wide" aria-label="Formación" ${UIEvents.attr("change", function(event,captured){StudentView.switchTab(this.value);}, [])}>
                        ${option('all', 'Todas las formaciones', typeFilteredStudents.length, this.currentFilter === 'all')}
                        ${typeGroups.map(([type, list]) => `<optgroup label="${Utils.sanitizeHTML(type.pluralName)}">${list.map(f => option(f.id, f.name, formationCount(f.id), this.currentFilter === f.id)).join('')}</optgroup>`).join('')}
                    </select>
                    <div class="search-box sv-search sl-search">
                        <i class="fas fa-search" aria-hidden="true"></i>
                        <input type="search" id="sv-search" autocomplete="off" aria-label="Buscar alumnos"
                               placeholder="Nombre, DNI, correo o usuario"
                               value="${Utils.sanitizeHTML(this.searchTerm)}"
                               ${UIEvents.attr("input", function(event,captured){StudentView.setSearch(this.value);}, [])}>
                    </div>
                </div>

                <div class="sl-layout ${onlyOwn ? 'is-single' : ''}">
                    <section class="dnr-card sl-card" id="sv-list-card" aria-label="Alumnos">
                        ${this.renderListCard(tabStudents, formations)}
                    </section>
                    ${onlyOwn ? '' : `<aside class="sl-aside" id="sl-aside">${this.renderAside(tabStudents, formations, typeFilteredStudents)}</aside>`}
                </div>
            </div>
        `;
    },

    // «2026-09-01» → «1 sept 2026»; any other text is shown as it was typed.
    shortDate(value) {
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
        if (!match) return String(value || '');
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    // Alumnos sin cuenta para entrar en la app: los que tienen correo se pueden dar de alta ya.
    hasAccess(student) { return !!AuthManager.getStudentUser(student.id); },
    pendingFilters: {
        noAccess: { label: 'Sin acceso a la app', test: s => s.active !== false && !!s.email && !StudentView.hasAccess(s) },
        noEmail: { label: 'Sin correo en la ficha', test: s => s.active !== false && !s.email && !StudentView.hasAccess(s) }
    },
    setPendingFilter(key) {
        this.pendingFilter = this.pendingFilter === key || !this.pendingFilters[key] ? null : key;
        this.currentPage = 1;
        this.selectedStudents.clear();
        this.repaintFilters();
    },

    renderStats(yearStudents, formations) {
        const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
        const active = yearStudents.filter(s => s.active !== false);
        const inactive = yearStudents.length - active.length;
        const nextYear = StudentService.getStudents().filter(s => this.yearOf(s) === this.currentYearStart + 1).length;
        const withoutAccess = active.filter(s => !this.hasAccess(s));
        const noEmail = withoutAccess.filter(s => !s.email).length;
        const colors = ['#0E6B62', '#4FD8C4', '#E2933C', '#14867A', '#24508A', '#B33A2E'];
        const byType = FormationTypeService.getTypes()
            .map(type => [type.pluralName, active.filter(s => (s.formations || []).some(fid => formations.find(f => f.id === fid)?.type === type.id)).length])
            .filter(([, n]) => n > 0);
        const stat = (label, value, sub, cls = '') => `<div class="sl-stat ${cls}"><span class="sl-stat-label">${label}</span><span class="sl-stat-value">${value}</span>${sub ? `<span class="sl-stat-sub">${sub}</span>` : ''}</div>`;
        const typeCard = byType.length ? `<div class="sl-stat">
                <span class="sl-stat-label">Por tipo de formación</span>
                <span class="sl-bar" role="img" aria-label="${Utils.sanitizeHTML(byType.map(([n, c]) => `${n} ${c}`).join(', '))}">${byType.map(([, n], i) => `<span style="flex: ${n}; background: ${colors[i % colors.length]}"></span>`).join('')}</span>
                <span class="sl-legend">${byType.map(([name, n], i) => `<span><i style="background: ${colors[i % colors.length]}"></i>${Utils.sanitizeHTML(name)} <strong>${n}</strong></span>`).join('')}</span>
            </div>` : stat('Por tipo de formación', '—', 'Sin alumnos con formación');
        const list = Array.isArray(this.admissionsList) ? this.admissionsList.filter(e => !e.alreadyApproved) : [];
        const counts = this.admissionCounts();
        const admissions = this.canSeeAdmissions()
            ? stat('Solicitudes por revisar', counts.reviewable, `${list.filter(e => e.status === 'interview').length} en entrevista · ${list.filter(e => e.status === 'payment').length} para activar pago`, counts.reviewable ? 'is-info' : '')
            : '';
        return stat('Alumnos este curso', `${active.length} <small>activos</small>`, `${plural(inactive, 'inactivo', 'inactivos')} · ${plural(nextYear, 'nuevo ingreso', 'nuevos ingresos')} para el curso siguiente`)
            + typeCard
            + stat('Sin acceso a la app', withoutAccess.length, withoutAccess.length ? `${noEmail} de ellos sin correo en la ficha` : 'Todos pueden entrar', withoutAccess.length ? 'is-warn' : 'is-ok')
            + admissions;
    },

    renderAside(tabStudents, formations, typeFilteredStudents) {
        const counts = this.admissionCounts();
        const item = (count, title, sub, tone, attrs, on = false) => `<button type="button" class="sl-todo ${on ? 'is-on' : ''}" ${on ? 'aria-pressed="true"' : ''} ${attrs}>
                <span class="sl-todo-count is-${tone}" aria-hidden="true">${count}</span>
                <span class="sl-todo-text"><strong>${title}</strong><span>${sub}</span></span>
                <i class="fas ${on ? 'fa-times' : 'fa-chevron-right'}" aria-hidden="true"></i>
            </button>`;
        const filterAttrs = key => UIEvents.attr("click", function(event,captured){StudentView.setPendingFilter(String(captured[0]));}, [key]);
        const sectionAttrs = step => UIEvents.attr("click", function(event,captured){StudentView.openAdmissionStep(String(captured[0]));}, [step]);
        const noAccess = tabStudents.filter(this.pendingFilters.noAccess.test).length;
        const noEmail = tabStudents.filter(this.pendingFilters.noEmail.test).length;
        const list = Array.isArray(this.admissionsList) ? this.admissionsList.filter(e => !e.alreadyApproved) : [];
        const by = status => list.filter(e => e.status === status).length;
        const todo = [
            item(noAccess, 'Sin acceso a la app', 'Tienen correo: se les puede dar ya', 'warn', filterAttrs('noAccess'), this.pendingFilter === 'noAccess'),
            item(noEmail, 'Sin correo en la ficha', 'Hace falta para darles acceso', 'danger', filterAttrs('noEmail'), this.pendingFilter === 'noEmail'),
            this.canSeeAdmissions() ? item(counts.reviewable, 'Solicitudes de admisión', `${by('pending')} pendientes · ${by('interview')} entrevista · ${by('payment')} pago`, 'teal', sectionAttrs('pending')) : '',
            this.canSeeAdmissions() ? item(counts.drafts, 'Matrículas a medias', 'Empezaron la solicitud y no la enviaron', 'mute', sectionAttrs('draft')) : ''
        ].join('');
        const formationRows = formations.map(f => [f, typeFilteredStudents.filter(s => (s.formations || []).includes(f.id)).length])
            .filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
        const max = formationRows[0]?.[1] || 1;
        return `
            <section class="dnr-card sl-side" aria-labelledby="sl-todo-title">
                <div class="sl-side-head"><h2 class="sl-h2" id="sl-todo-title">Pendiente</h2><span>Pulsa para filtrar la lista</span></div>
                ${todo}
            </section>
            <section class="dnr-card sl-side" aria-labelledby="sl-by-title">
                <h2 class="sl-h2" id="sl-by-title">Por formación</h2>
                ${formationRows.length ? `<div class="sl-by">${formationRows.map(([f, n]) => `<button type="button" class="sl-by-row ${this.currentFilter === f.id ? 'is-on' : ''}" ${UIEvents.attr("click", function(event,captured){StudentView.switchTab(StudentView.currentFilter === captured[0] ? 'all' : String(captured[0]));}, [f.id])}>
                    <span class="sl-by-label"><span>${Utils.sanitizeHTML(f.name)}</span><strong>${n}</strong></span>
                    <span class="sl-by-bar"><span style="width: ${Math.max(4, Math.round(n / max * 100))}%"></span></span>
                </button>`).join('')}</div>` : '<p class="sl-muted">No hay alumnos en este curso.</p>'}
            </section>`;
    },

    // Contenido de la tarjeta de la tabla: buscador (4b), paginación (5), barra de
    // selección, filas y paginador. Se usa en render() y, al escribir, en setSearch().
    renderListCard(tabStudents, formations) {
        // 4b. Buscador: dentro de lo filtrado, sin distinguir tildes ni mayúsculas
        const query = Utils.normalizeString(this.searchTerm.trim());
        let students = !query ? tabStudents : tabStudents.filter(s => this.matchesSearch(s, query));
        // 4b'. Filtro desde «Pendiente» (sin acceso / sin correo)
        const pending = this.canBrowseYears() && this.pendingFilters[this.pendingFilter];
        if (pending) students = students.filter(pending.test);

        // 4c. Coincidencias en otros cursos (solo quien puede cambiar de curso): aviso
        //     con acceso directo, sin mezclar cursos en la lista.
        const otherYears = new Map();
        if (query && this.canBrowseYears()) {
            StudentService.getStudents().forEach(s => {
                const year = this.yearOf(s);
                if (year !== this.currentYearStart && this.matchesSearch(s, query)) otherYears.set(year, (otherYears.get(year) || 0) + 1);
            });
        }
        const otherYearsHtml = otherYears.size === 0 ? '' : `
                    <div class="sv-search-hint" role="status">
                        <i class="fas fa-circle-info" aria-hidden="true"></i>
                        <span>En otros cursos:</span>
                        ${[...otherYears].sort((a, b) => a[0] - b[0]).map(([year, count]) => `
                        <button type="button" class="sv-search-hint-link" ${UIEvents.attr("click", function(event,captured){StudentView.showYearMatches(Number(captured[0]));}, [year])}>
                            ${count} coincidencia${count === 1 ? '' : 's'} en el curso ${year}/${year + 1} — Ver
                        </button>`).join('')}
                    </div>`;

        // 5. Paginación
        const totalStudents = students.length;
        const totalPages = Math.ceil(totalStudents / this.rowsPerPage) || 1;
        if (this.currentPage > totalPages) {
            this.currentPage = totalPages;
        }
        const startIdx = (this.currentPage - 1) * this.rowsPerPage;
        const paginatedStudents = students.slice(startIdx, startIdx + this.rowsPerPage);
        const n = this.selectedStudents.size;
        const canManage = this.canAddStudents();

        return `${otherYearsHtml}
                    <div class="sl-list-head" id="sl-list-head" ${n > 0 ? 'hidden' : ''}>
                        <h2 class="sl-h2">Alumnos · ${totalStudents}</h2>
                        ${pending ? `<button type="button" class="sl-chip" ${UIEvents.attr("click", function(event,captured){StudentView.setPendingFilter(String(captured[0]));}, [this.pendingFilter])}>Solo: ${Utils.sanitizeHTML(pending.label.toLowerCase())} <i class="fas fa-times" aria-hidden="true"></i><span class="dv-sr-only">Quitar filtro</span></button>`
                            : `<span class="sl-muted">Marca alumnos para darles acceso, promocionarlos o darlos de baja juntos</span>`}
                    </div>
                    <div id="sv-bulk-bar" class="sv-bulk-bar sl-bulk" role="region" aria-label="Acciones con los marcados" style="display: ${n > 0 ? 'flex' : 'none'};">
                        <span id="sv-bulk-count"><strong>${n}</strong> alumno${n === 1 ? '' : 's'} marcado${n === 1 ? '' : 's'}</span>
                        <div class="sv-bulk-actions">
                            ${AuthManager.isAdmin() ? `
                            <button type="button" class="sl-bulk-btn" ${UIEvents.attr("click", function(event,captured){StudentView.giveAccess(Array.from(StudentView.selectedStudents));}, [])}>
                                <i class="fas fa-key" aria-hidden="true"></i> Dar acceso
                            </button>
                            <button type="button" class="sl-bulk-btn" ${UIEvents.attr("click", function(event,captured){StudentView.sendAccess(Array.from(StudentView.selectedStudents));}, [])}>
                                <i class="fas fa-paper-plane" aria-hidden="true"></i> Enviar acceso
                            </button>` : ''}
                            <button type="button" class="sl-bulk-btn" ${UIEvents.attr("click", function(event,captured){StudentView.markSelectedNotContinuing();}, [])}>
                                <i class="fas fa-user-slash" aria-hidden="true"></i> No continúan
                            </button>
                            <button type="button" class="sl-bulk-btn is-primary" ${UIEvents.attr("click", function(event,captured){StudentView.openPromoteModal(Array.from(StudentView.selectedStudents));}, [])}>
                                <i class="fas fa-arrow-right" aria-hidden="true"></i> Promocionar a…
                            </button>
                            <button type="button" class="sl-bulk-close" aria-label="Quitar la selección" title="Quitar la selección" ${UIEvents.attr("click", function(event,captured){StudentView.clearSelection();}, [])}><i class="fas fa-times" aria-hidden="true"></i></button>
                        </div>
                    </div>
                    <div class="sl-scroll">
                        <table class="sl-table">
                            <thead>
                                <tr>
                                    <th scope="col" class="sl-col-check"><input type="checkbox" id="sv-select-all" aria-label="Marcar todos los de esta página" ${UIEvents.attr("change", function(event,captured){StudentView.toggleSelectAllStudents(this.checked);}, [])} title="Marcar todos (de esta página)"></th>
                                    <th scope="col">Alumno</th>
                                    <th scope="col" class="sl-col-formation">Formación</th>
                                    <th scope="col" class="sl-col-access">Acceso a la app</th>
                                    <th scope="col" class="sl-col-state">Estado</th>
                                    <th scope="col" class="sl-col-actions"><span class="dv-sr-only">Acciones</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                ${paginatedStudents.length > 0 ? paginatedStudents.map(s => this.renderRow(s, formations, canManage)).join('') : `<tr><td colspan="6"><div class="dnr-empty"><i class="fas fa-users-slash" style="font-size: 1.6rem; display: block; margin-bottom: 0.75rem;"></i>${query ? `Ningún alumno coincide con «${Utils.sanitizeHTML(this.searchTerm.trim())}»` : pending ? 'Nadie en esta situación con los filtros elegidos' : 'No hay alumnos para esta formación'}</div></td></tr>`}
                            </tbody>
                        </table>
                    </div>
                    ${this.renderPagination(totalStudents)}
        `;
    },

    renderRow(student, allFormations, canManage = this.canAddStudents()) {
        const sFormations = (student.formations || []).map(fId => allFormations.find(form => form.id === fId)?.name).filter(Boolean);

        const initials = this.getInitials(student.firstName, student.lastName);
        const userAccount = AuthManager.getStudentUser(student.id);
        // The username is data (escaped); "Sin cuenta" is our own markup (not escaped).
        const admin = AuthManager.isAdmin();
        const usernameCell = userAccount
            ? `<span class="sl-user"><code class="sv-username-code">${Utils.sanitizeHTML(userAccount.username)}</code>${admin ? `
                <button type="button" class="sl-mini-btn sv-send-access" aria-label="Enviar acceso por correo" title="Enviar por correo el usuario, una contraseña nueva y el enlace de acceso"
                        ${UIEvents.attr("click", function(event,captured){StudentView.sendAccess([String(captured[0])]);}, [student.id])}><i class="fas fa-paper-plane"></i></button>` : ''}</span>`
            : !student.email ? `<span class="sl-pill is-warn" title="Añade su correo en la ficha para poder darle acceso">Sin correo</span>`
            : admin ? `<button type="button" class="sl-give sv-give-access" title="Crear su cuenta para entrar en SICAF (no se envía ningún correo)"
                        ${UIEvents.attr("click", function(event,captured){StudentView.giveAccess([String(captured[0])]);}, [student.id])}><i class="fas fa-key"></i> Dar acceso</button>`
            : '<span class="sv-empty-cell">Sin cuenta</span>';
        const isChecked = this.selectedStudents.has(student.id);
        const contact = [student.email, student.phone].filter(Boolean).map(v => Utils.sanitizeHTML(v)).join(' · ') || 'Sin correo ni teléfono';
        const name = `${Utils.sanitizeHTML(student.firstName)} ${Utils.sanitizeHTML(student.lastName)}`;

        return `
            <tr class="sl-row ${!student.active ? 'sv-row-inactive' : ''} ${isChecked ? 'is-selected' : ''}">
                <td class="sl-col-check">
                    <input type="checkbox" class="sv-row-checkbox" aria-label="Marcar a ${name}" data-id="${Utils.sanitizeHTML(student.id)}" ${isChecked ? 'checked' : ''} ${UIEvents.attr("change", function(event,captured){StudentView.toggleSelectStudent(String(captured[0]),this.checked);}, [student.id])}>
                </td>
                <td>
                    <div class="sv-student-cell sl-student">
                        <button type="button" class="sv-avatar sl-avatar" aria-label="Ver foto de ${name}" ${UIEvents.attr("click", function(event,captured){StudentView.showPhotoPopup(String(captured[0]));}, [student.id])} title="Ver la foto en grande">
                            ${student.photo ? `<img data-student-photo="${Utils.sanitizeHTML(student.photo)}" alt="">` : initials}
                        </button>
                        <span class="sl-student-text">
                            <span class="sv-name-link" role="link" tabindex="0" ${UIEvents.attr("click", function(event,captured){App.navigate(String(captured[0]));}, [Utils.studentProfileUrl(student)])} ${UIEvents.attr("keydown", function(event,captured){if(event.key==='Enter')App.navigate(String(captured[0]));}, [Utils.studentProfileUrl(student)])} title="Ver ficha del alumno">
                                ${name}
                            </span>
                            <span class="sl-sub" title="${student.dni ? `DNI ${Utils.sanitizeHTML(student.dni)}` : ''}">${contact}</span>
                        </span>
                    </div>
                </td>
                <td class="sl-col-formation">
                    <span class="sl-formation">
                        <span title="${Utils.sanitizeHTML(sFormations.join(', '))}">${sFormations.length ? Utils.sanitizeHTML(sFormations[0]) + (sFormations.length > 1 ? ` <small>+${sFormations.length - 1}</small>` : '') : '<span class="sv-empty-cell">Sin formación</span>'}</span>
                        <span class="sl-mono">${student.enrollmentDate ? `Matrícula ${Utils.sanitizeHTML(this.shortDate(student.enrollmentDate))}` : 'Sin fecha de matrícula'}</span>
                    </span>
                </td>
                <td class="sl-col-access">${usernameCell}</td>
                <td class="sl-col-state">
                    <span class="sv-status-badge sl-pill ${student.active ? 'is-active' : 'is-inactive'}">
                        ${student.active ? 'Activo' : 'Inactivo'}
                    </span>
                </td>
                <td class="sl-col-actions">
                    <div class="sv-row-actions sl-actions">
                        ${canManage ? `<button type="button" class="sl-mini-btn is-teal" ${UIEvents.attr("click", function(event,captured){StudentView.openPromoteModal([String(captured[0])]);}, [student.id])} aria-label="Promocionar o trasladar" title="Trasladar / Promocionar a otra formación"><i class="fas fa-arrow-right"></i></button>` : ''}
                        <button type="button" class="sl-mini-btn" ${UIEvents.attr("click", function(event,captured){StudentView.openEditModal(String(captured[0]));}, [student.id])} aria-label="Editar" title="Editar"><i class="fas fa-pen"></i></button>
                        <details class="sl-more">
                            <summary class="sl-mini-btn" aria-label="Más acciones" title="Desactivar o eliminar"><i class="fas fa-ellipsis-h"></i></summary>
                            <div class="sl-more-menu">
                                <button type="button" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;StudentView.deactivateStudent(String(captured[0]));}, [student.id])}><i class="fas fa-user-slash"></i> Desactivar y bloquear acceso</button>
                                ${admin ? `<button type="button" class="is-danger" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;StudentView.deleteStudent(String(captured[0]));}, [student.id])}><i class="fas fa-trash"></i> Eliminar definitivamente</button>` : ''}
                            </div>
                        </details>
                    </div>
                </td>
            </tr>
        `;
    },

    // Shared by «Añadir» and «Editar alumno» (student null = new). Field names and ids are
    // the ones both save handlers read.
    // Ventana de alumno nuevo / editar (diseño «S»): bloques numerados a la izquierda (alumno,
    // contacto, matrícula) y a la derecha estado y acceso. Reutiliza las piezas .nf-* de la ficha de
    // personal. Los nombres de los campos no cambian: los leen el guardado y sus comprobaciones.
    renderStudentForm(student, formations, chatBlocked = false) {
        const isNew = !student;
        const value = key => Utils.sanitizeHTML(student?.[key] || '');
        const prefix = isNew ? 'form-' : 'edit-form-';
        const chosen = student?.formations || [];
        const admin = AuthManager.isAdmin();
        const user = isNew ? null : AuthManager.getStudentUser(student.id);
        // Each label points at its control through an id made from the field name.
        const field = (label, input, extra = '') => {
            const id = 'sv-f-' + /name="([^"]+)"/.exec(input)[1];
            return `<div class="form-group ${extra}"><label class="form-label" for="${id}">${label}</label>${input.replace(/^<(input|select)/, `<$1 id="${id}"`)}</div>`;
        };
        const step = (n, title, id) => `<div class="nf-step"><span class="nf-num" aria-hidden="true">${n}</span><h3 id="${id}">${title}</h3></div>`;
        const initials = isNew ? '' : Utils.sanitizeHTML(((student.firstName || '').charAt(0) + (student.lastName || '').charAt(0)).toUpperCase());
        // Formaciones agrupadas por tipo, en el orden de los tipos del centro.
        const types = FormationTypeService.getTypes();
        const groups = types.map(t => ({ name: t.pluralName || t.name, items: formations.filter(f => f.type === t.id) }))
            .concat([{ name: 'Otras', items: formations.filter(f => !types.some(t => t.id === f.type)) }])
            .filter(g => g.items.length);
        const chosenCount = formations.filter(f => chosen.includes(f.id)).length;
        const countText = n => n ? `${n} ${n === 1 ? 'elegida' : 'elegidas'}` : 'Ninguna elegida';
        const sw = (id, name, checked, title, sub) => `
            <label class="sv-state-row" for="${id}">
                <span class="fm-switch"><input type="checkbox" role="switch" name="${name}" id="${id}" ${checked ? 'checked' : ''}><span class="fm-track" aria-hidden="true"></span></span>
                <span><strong>${title}</strong><small>${sub}</small></span>
            </label>`;
        return `
            <div class="dnr-proposal students-form-dnr nf">
            <form id="${isNew ? 'add-student-form' : 'edit-student-form'}">
                <p class="nf-lead">Lo marcado con * es obligatorio.${isNew ? ' Por defecto, los alumnos nuevos se matriculan para el curso siguiente.' : ''}</p>
                <div class="nf-layout">
                    <div class="nf-main">
                        <section class="nf-card" aria-labelledby="sv-s1">
                            ${step(1, 'Alumno', 'sv-s1')}
                            <div class="sv-person">
                                <div class="sv-photo">
                                    <button type="button" id="photo-preview" class="sv-photo-btn" aria-label="${student?.photo ? 'Cambiar la foto' : 'Añadir foto'}" title="${student?.photo ? 'Cambiar la foto' : 'Añadir foto'}" ${UIEvents.attr("click", function(event,captured){document.getElementById('photo-input').click();}, [])}>
                                        ${student?.photo ? `<img data-student-photo="${Utils.sanitizeHTML(student.photo)}" alt="">` : (initials || '<i class="fas fa-camera" aria-hidden="true"></i><span>Añadir foto</span>')}
                                    </button>
                                    <input type="file" id="photo-input" accept="image/*" hidden ${UIEvents.attr("change", function(event,captured){StudentView.handlePhotoUpload(event);}, [])}>
                                    <input type="hidden" id="photo-data" name="photo" value="${value('photo')}">
                                    ${student?.photo ? `<button type="button" class="sv-photo-remove" ${UIEvents.attr("click", function(event,captured){StudentView.removePhoto();}, [])}>Quitar foto</button>` : ''}
                                </div>
                                <div class="nf-pair sv-person-fields">
                                    ${field('Nombre *', `<input type="text" name="firstName" class="form-input" required autocomplete="off" value="${value('firstName')}">`)}
                                    ${field('Apellidos *', `<input type="text" name="lastName" class="form-input" required autocomplete="off" value="${value('lastName')}">`)}
                                    ${field('DNI / NIE', `<input type="text" name="dni" class="form-input sv-mono" placeholder="12345678Z" value="${value('dni')}">`)}
                                    ${field('Año de nacimiento', `<input type="text" name="fechaNacimiento" class="form-input sv-mono" inputmode="numeric" maxlength="10" pattern="\\d{4}(-\\d{2}-\\d{2})?" title="Año con 4 cifras, por ejemplo 2004" placeholder="2004" value="${value('fechaNacimiento')}">`)}
                                </div>
                            </div>
                        </section>

                        <section class="nf-card" aria-labelledby="sv-s2">
                            ${step(2, 'Contacto', 'sv-s2')}
                            <div class="nf-grid">
                                ${field('Correo', `<input type="email" name="email" class="form-input" value="${value('email')}">`)}
                                ${field('Teléfono', `<input type="tel" name="phone" class="form-input" value="${value('phone')}">`)}
                                ${field('Municipio', `<input type="text" name="localidad" class="form-input" maxlength="200" value="${value('localidad')}">`)}
                                ${field('Dirección', `<input type="text" name="address" class="form-input" placeholder="Calle, número y código postal" value="${value('address')}">`, 'is-full')}
                            </div>
                            <p class="nf-note">Sin correo no se le puede dar acceso a la app.</p>
                        </section>

                        <section class="nf-card" aria-labelledby="sv-s3">
                            ${step(3, 'Matrícula', 'sv-s3')}
                            <div class="nf-grid">
                                ${field('Curso lectivo *', `<select name="academicYearStart" class="form-input" required>${this.renderYearOptionsForForm(isNew ? this.todayAcademicYearStart() + 1 : this.yearOf(student))}</select>`)}
                                ${field('Fecha de matrícula', `<input type="date" name="enrollmentDate" class="form-input" value="${isNew ? new Date().toISOString().split('T')[0] : value('enrollmentDate')}">`)}
                                ${field('Cómo nos ha conocido', `<input type="text" name="comoNosConociste" class="form-input" maxlength="500" value="${value('comoNosConociste')}">`)}
                            </div>
                            <fieldset class="sv-formation-picker" ${UIEvents.attr("change", function(event,captured){StudentView.countChosenFormations(this);}, [])}>
                                <legend>Formaciones en las que está matriculado <span id="sv-chosen-count">${countText(chosenCount)}</span></legend>
                                ${groups.map(g => `
                                    <div class="sv-chip-group" role="group" aria-label="${Utils.sanitizeHTML(g.name)}">
                                        <span class="sv-chip-group-name">${Utils.sanitizeHTML(g.name)}</span>
                                        ${g.items.map(f => `<label class="sv-chip" for="${prefix}${Utils.sanitizeHTML(f.id)}"><input type="checkbox" name="formations" value="${Utils.sanitizeHTML(f.id)}" id="${prefix}${Utils.sanitizeHTML(f.id)}" ${chosen.includes(f.id) ? 'checked' : ''}><span>${Utils.sanitizeHTML(f.name)}</span></label>`).join('')}
                                    </div>`).join('') || '<p class="nf-note">No hay formaciones.</p>'}
                            </fieldset>
                            <div class="nf-pair">
                                ${field('Estudios', `<input type="text" name="estudiosText" class="form-input" maxlength="2000" value="${value('estudiosText')}">`)}
                                ${field('Preferencias de curso', `<input type="text" name="coursePreferences" class="form-input" maxlength="500" placeholder="Edición / Foto / Dirección" value="${value('coursePreferences')}">`)}
                            </div>
                        </section>
                    </div>

                    <aside class="nf-aside">
                        ${isNew ? '' : `<section class="nf-card" aria-labelledby="sv-state-title">
                            <h3 class="nf-title" id="sv-state-title">Estado</h3>
                            ${sw('edit-active', 'active', student.active, 'Alumno activo', 'Si lo apagas, deja de salir en las listas del curso.')}
                            ${sw('edit-chatBlocked', 'chatBlocked', chatBlocked, 'Bloquear mensajes en Comunidad', 'Podrá leer, pero no escribir.')}
                        </section>`}
                        <section class="nf-card" aria-labelledby="sv-account-title">
                            <h3 class="nf-title" id="sv-account-title">Acceso a la app</h3>
                            ${user ? `<div class="sv-account-user"><span>Usuario</span><code class="sv-username-code">${Utils.sanitizeHTML(user.username || '')}</code></div>` : ''}
                            <div class="form-group">
                                <label class="form-label" for="userPassword">${user ? 'Nueva contraseña' : 'Contraseña de acceso (opcional)'}</label>
                                <input type="password" class="form-input" id="userPassword" name="userPassword" minlength="10" autocomplete="new-password" ${admin ? '' : 'disabled'} placeholder="${admin ? (user ? 'Vacío para dejar la que tiene' : 'Mínimo 10 caracteres') : 'Solo administración gestiona cuentas'}">
                            </div>
                            <p class="nf-note">${isNew ? 'Con contraseña se crea el usuario <strong>inicial.apellido</strong>. Lo normal es dejarla vacía y darle acceso después: le llega por correo.'
                                : 'Mínimo 10 caracteres. Lo normal es enviarle el acceso por correo desde su ficha; esto es solo para casos puntuales.'}</p>
                        </section>
                        ${isNew ? '' : '<section class="nf-card sv-after"><strong>Al guardar</strong><p>Se queda abierta su ficha con los cambios. Documentos, pago y tutor legal vienen de su solicitud de admisión.</p></section>'}
                    </aside>
                </div>
            </form>
            </div>`;
    },

    countChosenFormations(picker) {
        const n = picker.querySelectorAll('input[name="formations"]:checked').length;
        const out = picker.querySelector('#sv-chosen-count');
        if (out) out.textContent = n ? `${n} ${n === 1 ? 'elegida' : 'elegidas'}` : 'Ninguna elegida';
    },

    readExtraFields(formData) {
        return Object.fromEntries(['fechaNacimiento', 'localidad', 'estudiosText', 'comoNosConociste', 'coursePreferences']
            .map(key => [key, String(formData.get(key) || '').trim()]));
    },

    async saveOnce(action) {
        if (!AuthManager.requireModuleManage("alumnado",["admin","coordinator"])) return;
        if (this._savingStudent) return;
        this._savingStudent = true;
        try { await action(); }
        catch (error) { Utils.showToast(error.message || 'No se pudo guardar.', 'error'); }
        finally { this._savingStudent = false; }
    },

    openAddModal() {
        if (!AuthManager.requireModuleManage("alumnado",["admin","coordinator"])) return;
        const formations = FormationService.getFormations();

        const content = this.renderStudentForm(null, formations);

        ModalManager.show('Nuevo alumno', content, [
            {
                text: 'Cancelar',
                class: 'btn-secondary',
                action: () => ModalManager.close()
            },
            {
                text: 'Guardar',
                class: 'btn-primary',
                action: () => StudentView.saveOnce(async () => {
                    const form = document.getElementById('add-student-form');
                    if (form.dataset.photoLoading) { Utils.showToast('Espera a que termine de prepararse la foto.', 'info'); return; }
                    if (!form.checkValidity()) {
                        form.reportValidity();
                        return;
                    }
                    const formData = new FormData(form);
                    const selectedFormations = Array.from(form.querySelectorAll('input[name="formations"]:checked')).map(cb => cb.value);

                    const data = {
                        firstName: formData.get('firstName'),
                        lastName: formData.get('lastName'),
                        email: formData.get('email'),
                        phone: formData.get('phone'),
                        address: formData.get('address'),
                        ...this.readExtraFields(formData),
                        dni: formData.get('dni'),
                        photo: formData.get('photo'),
                        userPassword: formData.get('userPassword'),
                        enrollmentDate: formData.get('enrollmentDate'),
                        academicYearStart: parseInt(formData.get('academicYearStart'), 10),
                        formations: selectedFormations
                    };

                    const saved = await StudentService.createStudent(data);
                    if (saved?.success === false) {
                        Utils.showToast(saved.message || 'No se pudo crear la cuenta', 'error');
                        return;
                    }
                    Utils.showToast('Alumno creado correctamente', 'success');
                    ModalManager.close();
                    StudentView.currentYearStart = data.academicYearStart;
                    StudentView.repaintFilters();
                })
            }
        ]);
    },

    async openEditModal(studentId) {
        if (!AuthManager.requireModuleManage("alumnado",["admin","coordinator"])) return;
        console.log('Opening edit modal for student:', studentId); // Debug log
        const student = StudentService.getStudent(studentId);
        if (!student) return;

        let chatBlocked = false;
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/comunidad/bloqueados', { headers: { 'X-SICAF-CSRF': token } });
            const data = await res.json();
            if (data.success && Array.isArray(data.bloqueados)) {
                chatBlocked = data.bloqueados.includes(studentId);
            }
        } catch (e) {
            console.error('Error fetching blocked list:', e);
        }

        const formations = FormationService.getFormations();

        const content = this.renderStudentForm(student, formations, chatBlocked);

        ModalManager.show('Editar alumno', content, [
            {
                text: 'Cancelar',
                class: 'btn-secondary',
                action: () => ModalManager.close()
            },
            {
                text: 'Guardar cambios',
                class: 'btn-primary',
                action: () => StudentView.saveOnce(async () => {
                    const form = document.getElementById('edit-student-form');
                    if (form.dataset.photoLoading) { Utils.showToast('Espera a que termine de prepararse la foto.', 'info'); return; }
                    if (!form.checkValidity()) {
                        form.reportValidity();
                        return;
                    }
                    // Stored text must never add controls to this form: FormData and the
                    // checkboxes are read by name, so a duplicate means the form was altered.
                    const single = ['firstName','lastName','email','phone','address','dni','photo','userPassword','enrollmentDate','academicYearStart','active','chatBlocked'];
                    if (single.some(name => form.querySelectorAll(`[name="${name}"]`).length !== 1)
                        || form.querySelectorAll('#edit-active, #edit-chatBlocked').length !== 2
                        || form.querySelectorAll('input[name="formations"]').length !== formations.length) {
                        Utils.showToast('El formulario contiene datos no válidos. Recarga la página y revisa la ficha.', 'error');
                        return;
                    }
                    const formData = new FormData(form);
                    const selectedFormations = Array.from(form.querySelectorAll('input[name="formations"]:checked')).map(cb => cb.value);
                    const isActive = form.querySelector('#edit-active').checked;
                    const isChatBlocked = form.querySelector('#edit-chatBlocked').checked;

                    const data = {
                        firstName: formData.get('firstName'),
                        lastName: formData.get('lastName'),
                        email: formData.get('email'),
                        phone: formData.get('phone'),
                        address: formData.get('address'),
                        ...this.readExtraFields(formData),
                        dni: formData.get('dni'),
                        photo: formData.get('photo'),
                        userPassword: formData.get('userPassword'),
                        enrollmentDate: formData.get('enrollmentDate'),
                        academicYearStart: parseInt(formData.get('academicYearStart'), 10),
                        active: isActive,
                        formations: selectedFormations
                    };

                    const saved = await StudentService.updateStudent(studentId, data);
                    if (saved?.success === false) {
                        Utils.showToast(saved.message || 'No se pudo actualizar la cuenta', 'error');
                        return;
                    }

                    // This is a separate operation: never report a rejected change as saved.
                    let communityFailed = false;
                    if (isChatBlocked !== chatBlocked) try {
                        const token = AuthManager.getCsrfToken();
                        const blockUrl = isChatBlocked ? '/api/comunidad/bloquear' : '/api/comunidad/desbloquear';
                        const blockResponse = await fetch(blockUrl, {
                            method: 'POST',
                            headers: {
                                'X-SICAF-CSRF': token,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ studentId })
                        });
                        if (!blockResponse.ok) throw new Error('No se pudo cambiar el bloqueo de comunidad.');
                    } catch (e) {
                        communityFailed = true;
                    }

                    Utils.showToast(communityFailed ? 'Ficha guardada, pero el bloqueo de comunidad no se ha actualizado. Revisa ese cambio.' : 'Alumno actualizado correctamente', communityFailed ? 'warning' : 'success');
                    ModalManager.close();
                    StudentView.repaintFilters();
                })
            }
        ]);
    },

    async deleteStudent(studentId) {
        const result = await SafeHTML.fire({
            title: 'Eliminar alumno definitivamente',
            text: 'Se eliminarán su ficha, cuentas de acceso y archivos vinculados (DNI, fotos, contratos y trabajos). No se puede deshacer desde la app. Escribe ELIMINAR para confirmar.',
            input: 'text', showCancelButton: true, confirmButtonText: 'Eliminar definitivamente', cancelButtonText: 'Cancelar', focusCancel: true,
            inputValidator: value => String(value || '').trim().toUpperCase() === 'ELIMINAR' ? undefined : 'Escribe ELIMINAR para confirmar.'
        });
        if (!result.isConfirmed) return;
        await this.saveOnce(async () => {
            const saved = await StudentService.deleteStudent(studentId);
            if (!saved.success) { Utils.showToast(saved.message, 'error'); return; }
            AuthManager.clearCachedData();
            window.location.assign('/alumnado');
        });
    },

    async deactivateStudent(studentId) {
        const result = await SafeHTML.fire({
            title: '¿Desactivar alumno?',
            text: 'Se bloqueará su acceso y sus sesiones actuales. Se conservarán los documentos e historial. Al reactivarlo podrá iniciar sesión de nuevo.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, desactivar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
            focusCancel: true
        });

        if (result.isConfirmed) {
            const saved = await StudentService.updateStudent(studentId, { active: false });
            if (saved?.success === false) { Utils.showToast(saved.message, 'error'); return; }
            Utils.showToast('Alumno desactivado. Historial conservado.', 'success');
            StudentView.repaintFilters();
        }
    },

    async handlePhotoUpload(event) {
        const picked = event.target.files[0];
        const form = event.target.closest('form');
        event.target.value = ''; // The same file can be chosen again after cancelling.
        if (!picked) return;
        if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(picked.type) || picked.size > 10 * 1024 * 1024) {
            Utils.showToast('Selecciona PNG, JPG, GIF o WebP de hasta 10MB.', 'error');
            return;
        }
        form.dataset.photoLoading = '1';
        const file = await PhotoCropper.open(picked);
        delete form.dataset.photoLoading;
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            Utils.showToast('La foto encuadrada ocupa más de 2MB. Prueba con otra imagen.', 'error');
            return;
        }
        const preview = form.querySelector('#photo-preview');
        const photoData = form.querySelector('#photo-data');
        form.dataset.photoLoading = '1';
        try {
            const data = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(new Error('No se pudo leer la fotografía.'));
                reader.readAsDataURL(file);
            });
            if (photoData) photoData.value = data;
            if (preview) {
                const image = document.createElement('img');
                image.src = data;
                image.style.cssText = 'width:100%;height:100%;object-fit:cover;';
                preview.replaceChildren(image);
            }
            Utils.showToast('Foto preparada. Se guardará al confirmar la ficha.', 'info');
        } catch (error) {
            Utils.showToast(error.message, 'error');
        } finally {
            delete form.dataset.photoLoading;
        }
    },

    removePhoto() {
        const preview = document.getElementById('photo-preview');
        const photoData = document.getElementById('photo-data');
        const photoInput = document.getElementById('photo-input');

        if (preview) {
            SafeHTML.set(preview, '<i class="fas fa-camera" aria-hidden="true"></i><span>Añadir foto</span>');
            preview.closest('.sv-photo')?.querySelector('.sv-photo-remove')?.remove();
        }
        if (photoData) {
            photoData.value = '';
        }
        if (photoInput) {
            photoInput.value = '';
        }
        Utils.showToast('La foto se quitará al guardar la ficha.', 'info');
    },

    getInitials(firstName, lastName) {
        try {
            const first = firstName || '?';
            const last = lastName || '?';
            return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
        } catch {
            return '??';
        }
    },

    showPhotoPopup(studentId) {
        const student = StudentService.getStudent(studentId);
        if (!student) return;

        const initials = this.getInitials(student.firstName, student.lastName);
        const content = `
            <div style="text-align: center; padding: 1rem;">
                ${student.photo ?
                `<img data-student-photo="${Utils.sanitizeHTML(student.photo)}" style="max-width: 100%; max-height: 70vh; border-radius: 8px; box-shadow: var(--shadow-lg);">` :
                `<div class="professor-avatar-placeholder" style="width: 200px; height: 200px; margin: 0 auto; font-size: 4rem;">${Utils.sanitizeHTML(initials)}</div>`
            }
                <h3 style="margin-top: 1.5rem; font-size: 1.5rem;">${Utils.sanitizeHTML(student.firstName)} ${Utils.sanitizeHTML(student.lastName)}</h3>
                <p class="text-muted">${Utils.sanitizeHTML(student.dni || 'DNI no registrado')}</p>
            </div>
        `;

        ModalManager.show('Foto del Alumno', content, [
            {
                text: 'Cerrar',
                class: 'btn-secondary',
                action: () => ModalManager.close()
            }
        ]);
    },

    switchTypeTab(type) {
        this.currentTypeFilter = type;
        this.currentFilter = 'all'; // reset specific tab
        this.currentPage = 1;
        this.selectedStudents.clear();
        this.repaintFilters();
    },

    switchTab(formationId) {
        this.currentFilter = formationId;
        this.currentPage = 1;
        this.selectedStudents.clear();
        this.repaintFilters();
    },

    // How an application is named in the lists: the chosen formation, or — for a type whose
    // applicants order their preferences (Plan de Estudios → Tipos) — the type and first choice.
    applicationLabel(en, formations, {full = false} = {}) {
        if (FormationTypeService.ranksPreferences(en.tipoFormacion)) {
            const type = FormationTypeService.name(en.tipoFormacion);
            if (full) return `${type} (Preferencias: ${en.ordenDiplomaturas || 'No definidas'})`;
            const first = en.ordenDiplomaturas ? en.ordenDiplomaturas.split('|')[0].replace(/^\s*\d+\.\s*/, '').trim() : '-';
            return `${type} (Pref: ${first})`;
        }
        return formations.find(f => f.id === en.formationId)?.name || 'Formación desconocida';
    },

    // Courses of the chosen type in the quick formation form (admissions: the first one).
    fillAdmissionYears(typeSelect) {
        const select = typeSelect.form?.querySelector('select[name="academicYear"]');
        if (!select) return;
        const years = FormationTypeService.years(typeSelect.value);
        SafeHTML.set(select, years.length
            ? years.map((y, i) => `<option value="${Utils.sanitizeHTML(y.id)}" ${i === 0 ? 'selected' : ''}>${Utils.sanitizeHTML(y.name)}${i === 0 ? ' (recomendado para admisiones)' : ''}</option>`).join('')
            : '<option value="">No aplica</option>');
    },

    // Solicitudes "para revisar" (anteriores al control de duplicados): antes de
    // aprobarlas hay que decidir si es un alumno nuevo o si ya existe. Si hay alumnos
    // con el mismo DNI o email se ofrece vincularla a uno de ellos (se vincula aquí);
    // si no, se pide confirmar el alta nueva. Devuelve el cuerpo extra para
    // /api/enrollments/approve ({ confirmNewStudent: true } o {}), o null si se cancela.
    async resolveEnrollmentLink(enrollmentId, applicant = {}) {
        if (!applicant.requiresLinkReview) return {};
        const norm = value => Utils.normalizeString(String(value || '')).replace(/\s+/g, '');
        const dni = norm(applicant.dni), email = norm(applicant.email);
        const matches = (StudentService.getStudents() || []).filter(s => (dni && norm(s.dni) === dni) || (email && norm(s.email) === email));
        const name = Utils.sanitizeHTML(applicant.name || 'este solicitante');
        if (!matches.length) {
            const result = await SafeHTML.fire({
                icon: 'warning',
                title: 'Solicitud pendiente de revisión',
                html: `Esta solicitud es anterior al control de alumnos duplicados.<br>No hay ningún alumno con el mismo DNI o email que <b>${name}</b>.<br><br>¿Crear un <b>alumno nuevo</b> al aprobarla?`,
                showCancelButton: true,
                confirmButtonText: 'Sí, crear alumno nuevo',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#10b981',
                cancelButtonColor: '#64748b'
            });
            return result.isConfirmed ? { confirmNewStudent: true } : null;
        }
        const result = await SafeHTML.fire({
            icon: 'warning',
            title: 'Posible alumno ya registrado',
            html: `El DNI o el email de <b>${name}</b> coincide con ${matches.length === 1 ? 'un alumno' : 'estos alumnos'} de Alumnado.<br>Vincula la solicitud para no crear un duplicado, o crea un alumno nuevo si es otra persona.`,
            input: 'select',
            // SweetAlert writes option labels as HTML.
            inputOptions: Object.fromEntries(matches.map(s => [s.id, Utils.sanitizeHTML(`${s.firstName || ''} ${s.lastName || ''}${s.dni ? ' · ' + s.dni : ''}${s.email ? ' · ' + s.email : ''}`.trim())])),
            showDenyButton: true,
            showCancelButton: true,
            confirmButtonText: 'Vincular a este alumno',
            denyButtonText: 'Crear alumno nuevo',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b'
        });
        if (result.isDenied) return { confirmNewStudent: true };
        if (!result.isConfirmed || !result.value) return null;
        try {
            const response = await fetch(`/api/enrollments/${encodeURIComponent(enrollmentId)}/link-student`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() },
                body: JSON.stringify({ studentId: result.value, confirm: true })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) throw new Error(data.error || 'No se pudo vincular la solicitud.');
            return {};
        } catch (error) {
            await SafeHTML.fire('Error', error.message, 'error');
            return null;
        }
    },

    matchesSearch(student, query) {
        return [`${student.firstName || ''} ${student.lastName || ''}`, student.dni, student.email, student.phone, AuthManager.getStudentUser(student.id)?.username]
            .some(value => Utils.normalizeString(String(value || '')).includes(query));
    },

    // "Ver" del aviso de otros cursos: ir a ese curso con la búsqueda puesta y sin
    // filtros de tipo/formación, para que las coincidencias se vean seguro.
    showYearMatches(year) {
        if (!this.canBrowseYears() || !Number.isInteger(year)) return;
        this.currentTypeFilter = 'all';
        this.currentFilter = 'all';
        this.setAcademicYear(String(year));
    },

    // Al escribir se repinta solo la tarjeta de la tabla: la caja de búsqueda, los
    // filtros y el resto de la vista no se tocan, así que ni salta la página ni se
    // pierde el foco. Sin contexto (primera carga) se repinta la vista completa.
    setSearch(value) {
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => {
            this.searchTerm = value;
            this.currentPage = 1;
            this.selectedStudents.clear();
            const card = document.getElementById('sv-list-card');
            if (card && this._listContext) {
                SafeHTML.set(card, this.renderListCard(this._listContext.tabStudents, this._listContext.formations));
            } else {
                this.repaintFilters();
            }
        }, 150);
    },

    // ==========================================
    // IMPORTAR ALUMNOS DESDE EXCEL Y DAR ACCESO
    // La importación crea fichas sin cuenta ni correo. Admin da acceso (sin
    // correo) y, aparte, envía usuario + contraseña nueva + enlace de entrada.
    // ==========================================

    openImportModal() {
        if (!this.canImportStudents()) return;
        this._import = null;
        const formations = FormationService.getFormations();
        const types = FormationTypeService.getTypes();
        const groups = types.map(type => [type.pluralName, formations.filter(f => f.type === type.id)])
            .concat([['Otras', formations.filter(f => !types.some(type => type.id === f.type))]]).filter(([, list]) => list.length);
        const year = this.currentYearStart ?? this.todayAcademicYearStart();
        const columns = ['Nombre', 'Apellidos', 'DNI', 'Teléfono', 'Mail', 'Año de nacimiento', 'Municipio', 'Estudios', 'Cómo nos ha conocido', 'Fecha de matriculación', 'Dirección', 'Preferencias de curso'];
        ModalManager.show('Importar alumnos desde Excel', `
            <div class="dnr-proposal students-form-dnr sv-import">
                <p class="sv-form-hint sv-import-intro"><i class="fas fa-circle-info"></i> Se crean las fichas <strong>sin cuenta y sin enviar ningún correo</strong>. Después, administración les da acceso y les envía sus datos cuando quiera.</p>

                <div class="dnr-divider"><i class="fas fa-graduation-cap"></i> 1 · Destino</div>
                <div class="sv-form-pair">
                    <div class="form-group">
                        <label class="form-label" for="sv-import-formation">Formación *</label>
                        <select class="form-input" id="sv-import-formation" required>
                            <option value="">Elige la formación…</option>
                            ${groups.map(([label, list]) => `<optgroup label="${Utils.sanitizeHTML(label)}">${list.map(f => `<option value="${Utils.sanitizeHTML(f.id)}" ${this.currentFilter === f.id ? 'selected' : ''}>${Utils.sanitizeHTML(f.name)}</option>`).join('')}</optgroup>`).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="sv-import-year">Curso académico *</label>
                        <select class="form-input" id="sv-import-year">${this.renderYearOptionsForForm(year)}</select>
                    </div>
                </div>

                <div class="dnr-divider"><i class="fas fa-file-excel"></i> 2 · Archivo</div>
                <label class="sv-dropzone" id="sv-import-drop" for="sv-import-file"
                       ${UIEvents.attr("dragover", function(event,captured){event.preventDefault();this.classList.add('is-over');}, [])}
                       ${UIEvents.attr("dragleave", function(event,captured){this.classList.remove('is-over');}, [])}
                       ${UIEvents.attr("drop", function(event,captured){StudentView.dropImportFile(event);}, [])}>
                    <i class="fas fa-file-arrow-up" aria-hidden="true"></i>
                    <span class="sv-dropzone-title" id="sv-import-filename">Elige el archivo Excel o arrástralo aquí</span>
                    <span class="sv-form-hint">Solo .xlsx, hasta 2 MB</span>
                    <input type="file" id="sv-import-file" class="sv-visually-hidden" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                           ${UIEvents.attr("change", function(event,captured){StudentView.previewImport(this);}, [])}>
                </label>
                <div class="sv-import-columns">
                    <span class="form-label">Columnas que se leen (por su título, en cualquier orden)</span>
                    <div class="sv-import-chips">${columns.map(c => `<span class="sv-import-chip">${c}</span>`).join('')}</div>
                    <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){PrivateDownloads.download('/api/students/import/template');}, [])}><i class="fas fa-download"></i> Descargar plantilla</button>
                </div>

                <div class="dnr-divider"><i class="fas fa-list-check"></i> 3 · Revisión</div>
                <div id="sv-import-preview" aria-live="polite"><p class="sv-form-hint">Aquí verás cada alumno del archivo antes de crear nada.</p></div>

                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="button" class="btn btn-primary" id="sv-import-submit" disabled ${UIEvents.attr("click", function(event,captured){StudentView.confirmImport();}, [])}>
                        <i class="fas fa-file-import"></i> Importar
                    </button>
                </div>
            </div>`);
    },

    // A file dropped on the zone goes through the same input, so preview and import read it.
    dropImportFile(event) {
        event.preventDefault();
        event.currentTarget.classList.remove('is-over');
        const file = event.dataTransfer?.files?.[0];
        const input = document.getElementById('sv-import-file');
        if (!file || !input) return;
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
        this.previewImport(input);
    },

    readFileBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
            reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
            reader.readAsDataURL(file);
        });
    },

    async previewImport(input) {
        const file = input.files[0];
        const preview = document.getElementById('sv-import-preview');
        const submit = document.getElementById('sv-import-submit');
        this._import = null;
        submit.disabled = true;
        const name = document.getElementById('sv-import-filename');
        if (name) name.textContent = file ? file.name : 'Elige el archivo Excel o arrástralo aquí';
        document.getElementById('sv-import-drop')?.classList.toggle('has-file', !!file);
        if (!file) { SafeHTML.set(preview, ''); return; }
        if (file.size > 2 * 1024 * 1024) { SafeHTML.set(preview, '<p class="sv-import-error">El archivo ocupa más de 2 MB.</p>'); return; }
        SafeHTML.set(preview, '<p class="sv-import-help"><i class="fas fa-spinner fa-spin"></i> Leyendo el archivo…</p>');
        try {
            const base64 = await this.readFileBase64(file);
            const result = await StudentService.previewImport(base64);
            if (!result.success) throw new Error(result.message);
            if (input.files[0] !== file) return; // Another file was chosen meanwhile.
            const ok = result.rows.filter(row => !row.errors.length && !row.duplicate);
            this._import = { file: base64, count: ok.length };
            const status = row => row.errors.length ? `<span class="sv-import-state is-error">${Utils.sanitizeHTML(row.errors.join('. '))}</span>`
                : row.duplicate ? `<span class="sv-import-state is-skip">${Utils.sanitizeHTML(row.duplicate)}</span>`
                : row.warnings.length ? `<span class="sv-import-state is-warn">Se creará · ${Utils.sanitizeHTML(row.warnings.join('. '))}</span>`
                : '<span class="sv-import-state is-ok">Se creará</span>';
            SafeHTML.set(preview, `
                <p class="sv-import-summary"><strong>${ok.length}</strong> de ${result.rows.length} alumnos se crearán${result.rows.length > ok.length ? ` · ${result.rows.length - ok.length} se saltarán` : ''}.</p>
                <div class="data-table sv-import-table"><table>
                    <thead><tr><th>Fila</th><th>Nombre</th><th>Apellidos</th><th>DNI</th><th>Correo</th><th>Estado</th></tr></thead>
                    <tbody>${result.rows.map(row => `<tr><td>${row.row}</td><td>${Utils.sanitizeHTML(row.data.firstName || '—')}</td><td>${Utils.sanitizeHTML(row.data.lastName || '—')}</td>
                        <td>${Utils.sanitizeHTML(row.data.dni || '—')}</td><td>${Utils.sanitizeHTML(row.data.email || '—')}</td><td>${status(row)}</td></tr>`).join('')}</tbody>
                </table></div>`);
            submit.disabled = ok.length === 0;
            SafeHTML.set(submit, `<i class="fas fa-file-import"></i> Importar ${ok.length} alumno${ok.length === 1 ? '' : 's'}`);
        } catch (error) {
            SafeHTML.set(preview, `<p class="sv-import-error">${Utils.sanitizeHTML(error.message || 'No se pudo leer el archivo.')}</p>`);
        }
    },

    async confirmImport() {
        const formationId = document.getElementById('sv-import-formation').value;
        const year = Number(document.getElementById('sv-import-year').value);
        const submit = document.getElementById('sv-import-submit');
        if (!this._import || this._importing) return;
        if (!formationId) { Utils.showToast('Elige la formación.', 'warning'); document.getElementById('sv-import-formation').focus(); return; }
        this._importing = true;
        submit.disabled = true;
        try {
            const result = await StudentService.importStudents(this._import.file, formationId, year);
            if (!result.success) throw new Error(result.message);
            Utils.showToast(`${result.created} alumno${result.created === 1 ? '' : 's'} importado${result.created === 1 ? '' : 's'}${result.skipped ? ` · ${result.skipped} saltado${result.skipped === 1 ? '' : 's'}` : ''}`, 'success');
            ModalManager.closeModal();
            // Show where they went.
            this.currentYearStart = year;
            this.currentTypeFilter = 'all';
            this.currentFilter = formationId;
            this.currentPage = 1;
            this.repaintFilters();
        } catch (error) {
            Utils.showToast(error.message || 'No se pudo importar.', 'error');
            submit.disabled = false;
        } finally {
            this._importing = false;
        }
    },

    async giveAccess(ids) {
        if (!AuthManager.isAdmin() || this._accessBusy) return;
        const withoutAccess = ids.filter(id => !AuthManager.getStudentUser(id));
        if (!withoutAccess.length) { Utils.showToast('Ya tienen acceso.', 'info'); return; }
        // Without an e-mail the student could never receive the credentials or reset the password.
        const pending = withoutAccess.filter(id => StudentService.getStudent(id)?.email);
        const noEmail = withoutAccess.length - pending.length;
        if (!pending.length) { Utils.showToast('Sin correo no se puede dar acceso. Añade el correo en la ficha.', 'warning'); return; }
        this._accessBusy = true;
        try {
            const result = await StudentService.giveAccess(pending);
            if (!result.success) throw new Error(result.message);
            const skipped = noEmail + (result.skipped || 0);
            Utils.showToast(`Acceso creado para ${result.created} alumno${result.created === 1 ? '' : 's'}. No se ha enviado ningún correo.${skipped ? ` ${skipped} sin correo, sin acceso.` : ''}`, skipped ? 'warning' : 'success');
            this.repaintFilters();
        } catch (error) {
            Utils.showToast(error.message || 'No se pudo dar acceso.', 'error');
        } finally {
            this._accessBusy = false;
        }
    },

    async sendAccess(ids) {
        if (!AuthManager.isAdmin() || this._accessBusy) return;
        const students = ids.map(id => StudentService.getStudent(id)).filter(Boolean);
        const ready = students.filter(s => AuthManager.getStudentUser(s.id) && s.email);
        if (!ready.length) { Utils.showToast('Ningún alumno seleccionado tiene acceso y correo. Dales acceso primero.', 'warning'); return; }
        const others = students.length - ready.length;
        const choice = await SafeHTML.fire({
            title: ready.length === 1 ? `¿Enviar acceso a ${ready[0].firstName}?` : `¿Enviar acceso a ${ready.length} alumnos?`,
            html: `Recibirá${ready.length === 1 ? '' : 'n'} por correo su usuario, una <b>contraseña nueva</b> y el enlace para entrar. Si ya tenía${ready.length === 1 ? '' : 'n'} contraseña, la anterior dejará de valer.${others ? `<br><br>${others} alumno${others === 1 ? '' : 's'} sin acceso o sin correo no lo recibirá${others === 1 ? '' : 'n'}.` : ''}`,
            icon: 'question', showCancelButton: true, confirmButtonColor: '#0d9488', cancelButtonColor: '#64748b',
            confirmButtonText: 'Enviar', cancelButtonText: 'Cancelar'
        });
        if (!choice.isConfirmed) return;
        this._accessBusy = true;
        try {
            const result = await StudentService.sendAccess(ready.map(s => s.id));
            if (!result.success) throw new Error(result.message);
            const failed = result.skipped.map(item => `${item.name}: ${item.reason}`);
            Utils.showToast(`Acceso enviado a ${result.sent} alumno${result.sent === 1 ? '' : 's'}${failed.length ? `. Sin enviar: ${failed.join('; ')}` : ''}`, failed.length ? 'warning' : 'success');
        } catch (error) {
            Utils.showToast(error.message || 'No se pudo enviar el acceso.', 'error');
        } finally {
            this._accessBusy = false;
        }
    },

    // ==========================================
    // SELECCIÓN Y PROMOCIÓN DE CURSO EN BLOQUE
    // (traslado de alumnos de una formación/curso a otra, p.ej. Dirección 1º →
    // Dirección 2º al empezar curso nuevo, o casos sueltos entre pistas distintas
    // como Dirección 1º → Foto 2º)
    // ==========================================

    toggleSelectStudent(id, checked) {
        if (checked) {
            this.selectedStudents.add(id);
        } else {
            this.selectedStudents.delete(id);
        }
        const row = document.querySelector(`.sv-row-checkbox[data-id="${id}"]`)?.closest('tr');
        if (row) row.classList.toggle('is-selected', checked);

        const selectAll = document.getElementById('sv-select-all');
        if (selectAll) {
            const boxes = document.querySelectorAll('.sv-row-checkbox');
            selectAll.checked = boxes.length > 0 && Array.from(boxes).every(cb => cb.checked);
        }
        this.updateBulkBar();
    },

    toggleSelectAllStudents(checked) {
        document.querySelectorAll('.sv-row-checkbox').forEach(cb => {
            const id = cb.dataset.id;
            cb.checked = checked;
            if (checked) this.selectedStudents.add(id); else this.selectedStudents.delete(id);
            const row = cb.closest('tr');
            if (row) row.classList.toggle('is-selected', checked);
        });
        this.updateBulkBar();
    },

    clearSelection() {
        this.selectedStudents.clear();
        this.refreshList();
    },

    // Actualiza el contador/visibilidad de la barra sin re-renderizar toda la tabla
    // (igual que LeadsView.updateBulkEmailButton) — así no se pierde el scroll ni el
    // estado de los checkboxes ya marcados.
    updateBulkBar() {
        const bar = document.getElementById('sv-bulk-bar');
        const countEl = document.getElementById('sv-bulk-count');
        const n = this.selectedStudents.size;
        if (bar) bar.style.display = n > 0 ? 'flex' : 'none';
        const head = document.getElementById('sl-list-head');
        if (head) head.hidden = n > 0;
        if (countEl) SafeHTML.set(countEl, `<strong>${n}</strong> alumno${n === 1 ? '' : 's'} marcado${n === 1 ? '' : 's'}`);
    },

    // Modal de traslado/promoción — se usa tanto desde el botón de fila (un solo
    // alumno) como desde la barra de selección en bloque (varios a la vez). Cambia
    // la formación y adelanta el curso lectivo al siguiente respecto al que tenga
    // cada alumno (no hace falta que todos vengan del mismo curso, aunque en la
    // práctica casi siempre es así).
    openPromoteModal(studentIds) {
        if (!AuthManager.requireModuleManage("alumnado",["admin","coordinator"])) return;
        if (!studentIds || studentIds.length === 0) return;
        const students = studentIds.map(id => StudentService.getStudent(id)).filter(Boolean);
        if (students.length === 0) return;

        const formations = FormationService.getFormations();
        const namesPreview = students.slice(0, 4).map(s => `${s.firstName} ${s.lastName}`).join(', ') + (students.length > 4 ? ` y ${students.length - 4} más` : '');

        const content = `
            <div class="dnr-proposal students-form-dnr">
                <p style="margin: 0 0 1.25rem; color: var(--dnr-ink-mute); font-size: 0.88rem;">
                    Vas a trasladar a <strong style="color: var(--dnr-ink);">${students.length} alumno${students.length === 1 ? '' : 's'}</strong> (${Utils.sanitizeHTML(namesPreview)}) a una nueva formación y curso.
                </p>
                <form id="promote-students-form">
                    <div class="form-group">
                        <label class="form-label">Formación destino *</label>
                        <select name="targetFormationId" class="form-input" required>
                            <option value="">-- Elige formación --</option>
                            ${formations.map(f => `<option value="${Utils.sanitizeHTML(f.id)}">${Utils.sanitizeHTML(f.name)}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Curso lectivo destino *</label>
                        <select name="targetYearStart" class="form-input" required>
                            ${this.renderYearOptionsForForm(this.todayAcademicYearStart() + 1)}
                        </select>
                        <small class="text-muted" style="display: block; margin-top: 0.4rem;">
                            <i class="fas fa-info-circle"></i> Por defecto, el curso siguiente al actual
                        </small>
                    </div>
                </form>
            </div>
        `;

        ModalManager.show('Promocionar Alumnos', content, [
            { text: 'Cancelar', class: 'btn-secondary', action: () => ModalManager.close() },
            {
                text: 'Trasladar',
                class: 'btn-primary',
                action: () => StudentView.saveOnce(async () => {
                    const form = document.getElementById('promote-students-form');
                    if (!form.checkValidity()) { form.reportValidity(); return; }
                    const formData = new FormData(form);
                    const targetFormationId = formData.get('targetFormationId');
                    const targetYearStart = parseInt(formData.get('targetYearStart'), 10);

                    const saved = await StudentService.updateStudents(students.map(s => s.id), {
                        formations: [targetFormationId], academicYearStart: targetYearStart
                    });
                    if (!saved.success) { Utils.showToast(saved.message || saved.error, 'error'); return; }

                    // Consolida en Prematrícula el estado económico de cada alumno
                    // promocionado (saca su registro de "Previsión" si venía de la
                    // previsión en masa, o lo crea si no existía). Best-effort: un
                    // fallo aquí no debe deshacer ni bloquear el traslado académico,
                    // que ya se ha guardado justo arriba.
                    const token = AuthManager.getCsrfToken();
                    const consolidated = await Promise.all(students.map(s =>
                        fetch('/api/pre-registration/consolidate-promotion', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-SICAF-CSRF': token
                            },
                            body: JSON.stringify({
                                studentId: s.id,
                                formationId: targetFormationId,
                                academicYearStart: targetYearStart
                            })
                        }).then(response => response.ok).catch(() => false)
                    ));

                    ModalManager.close();
                    Utils.showToast(`${students.length} alumno${students.length === 1 ? '' : 's'} trasladado${students.length === 1 ? '' : 's'} correctamente`, 'success');
                    if (consolidated.some(ok => !ok)) Utils.showToast('El traslado académico está guardado, pero falta revisar la consolidación económica en Prematrícula.', 'warning');
                    StudentView.selectedStudents.clear();
                    StudentView.currentYearStart = targetYearStart;
                    StudentView.currentFilter = 'all';
                    StudentView.repaintFilters();
                })
            }
        ]);
    },

    // "No continúa el curso" — no traslada ni cambia el curso lectivo, solo marca al
    // alumno como inactivo (mismo campo que el toggle "Alumno en estado Activo" del
    // modal de edición, aquí accesible directamente desde la selección en bloque).
    markSelectedNotContinuing() {
        const ids = Array.from(this.selectedStudents);
        if (ids.length === 0) return;
        const students = ids.map(id => StudentService.getStudent(id)).filter(Boolean);
        const namesPreview = students.slice(0, 4).map(s => `${s.firstName} ${s.lastName}`).join(', ') + (students.length > 4 ? ` y ${students.length - 4} más` : '');

        SafeHTML.fire({
            title: '¿Marcar como que no continúan?',
            html: `<strong>${students.length}</strong> alumno${students.length === 1 ? '' : 's'} (${Utils.sanitizeHTML(namesPreview)}) quedará${students.length === 1 ? '' : 'n'} marcado${students.length === 1 ? '' : 's'} como <b>Inactivo</b> y sin acceso a la app. No se les cambia de formación ni de curso.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--dnr-danger, #D6483C)',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, no continúan',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            const saved = await StudentService.updateStudents(students.map(s => s.id), { active: false });
            if (!saved.success) { Utils.showToast(saved.message || saved.error, 'error'); return; }
            Utils.showToast(`${students.length} alumno${students.length === 1 ? '' : 's'} marcado${students.length === 1 ? '' : 's'} como no continúa${students.length === 1 ? '' : 'n'}`, 'success');
            this.selectedStudents.clear();
            this.repaintFilters();
        });
    },

    // ==========================================
    // SECCIÓN DE SOLICITUDES DE ADMISIÓN
    // ==========================================

    init() {
        this.updateBadgeCount();
    },

    changePage(page) {
        this.currentPage = page;
        this.refreshList();
    },

    changeRowsPerPage(val) {
        this.rowsPerPage = parseInt(val, 10);
        this.currentPage = 1;
        this.refreshList();
    },

    refreshSide() {
        if (!document.getElementById('sl-stats') && !document.getElementById('sl-aside')) return;
        const fresh = document.createElement('div');
        SafeHTML.set(fresh, this.render());
        for (const selector of ['#sl-stats', '#sl-aside']) {
            const now = document.querySelector(selector), next = fresh.querySelector(selector);
            if (now && next) now.replaceWith(next);
        }
    },

    // Solo la tarjeta de la tabla (paginar, seleccionar): sin contexto se repinta lo demás.
    refreshList() {
        const card = document.getElementById('sv-list-card');
        if (card && this._listContext) SafeHTML.set(card, this.renderListCard(this._listContext.tabStudents, this._listContext.formations));
        else this.repaintFilters();
    },

    renderPagination(totalStudents) {
        if (totalStudents === 0) return '';

        const totalPages = Math.ceil(totalStudents / this.rowsPerPage) || 1;
        const startIdx = (this.currentPage - 1) * this.rowsPerPage + 1;
        const endIdx = Math.min(this.currentPage * this.rowsPerPage, totalStudents);

        const options = [10, 20, 50, 100, 200];
        const rowsSelectHtml = options.map(opt => `
            <option value="${opt}" ${this.rowsPerPage === opt ? 'selected' : ''}>${opt}</option>
        `).join('');

        let pageNumbers = [];
        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            if (this.currentPage <= 3) {
                pageNumbers = [1, 2, 3, 4, '...', totalPages];
            } else if (this.currentPage >= totalPages - 2) {
                pageNumbers = [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
            } else {
                pageNumbers = [1, '...', this.currentPage - 1, this.currentPage, this.currentPage + 1, '...', totalPages];
            }
        }

        const pagesHtml = pageNumbers.map(page => {
            if (page === '...') {
                return `<span style="padding: 0 6px; color: var(--dnr-ink-faint); font-size: 0.8rem; user-select: none;">...</span>`;
            }
            const isActive = this.currentPage === page;
            return `
                <button type="button" class="sv-page-btn ${isActive ? 'active' : ''}" ${UIEvents.attr("click", function(event,captured){StudentView.changePage(captured[0]);}, [page])}>
                    ${page}
                </button>
            `;
        }).join('');

        return `
            <div class="sv-pagination">
                <div class="sv-pagination-info">
                    Mostrando <strong>${startIdx}-${endIdx}</strong> de <strong>${totalStudents}</strong> alumnos
                </div>

                <div style="display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                        <span class="sv-pagination-info">Filas por página:</span>
                        <select class="sv-rows-select" ${UIEvents.attr("change", function(event,captured){StudentView.changeRowsPerPage(this.value);}, [])}>
                            ${rowsSelectHtml}
                        </select>
                    </div>

                    <div style="display: flex; gap: 6px; align-items: center;">
                        <button type="button" class="sv-page-btn" ${this.currentPage === 1 ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){StudentView.changePage(captured[0]);}, [this.currentPage - 1])}>
                            <i class="fas fa-chevron-left" style="font-size: 0.8rem;"></i>
                        </button>

                        ${pagesHtml}

                        <button type="button" class="sv-page-btn" ${this.currentPage === totalPages ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){StudentView.changePage(captured[0]);}, [this.currentPage + 1])}>
                            <i class="fas fa-chevron-right" style="font-size: 0.8rem;"></i>
                        </button>
                    </div>
                </div>
            </div>
        `;
    },

    // ==========================================
    // ADMISIONES (diseño «O · Admisiones: embudo de solicitudes»): pasos con su número arriba,
    // la lista del paso elegido a la izquierda y la solicitud elegida a la vista a la derecha.
    // Sustituye a las antiguas «Solicitudes» y «En proceso» (borradores y rechazadas).
    // ==========================================

    ADMISSION_STEPS: [
        { id: 'draft', label: 'A medias', sub: 'Empezaron y no enviaron', pill: 'Borrador', tone: 'mute', empty: 'Nadie tiene una matrícula a medias ahora mismo.' },
        { id: 'pending', label: 'Pendientes', sub: 'Por revisar', pill: 'Pendiente', tone: 'warn', empty: 'No hay solicitudes pendientes de revisar.' },
        { id: 'interview', label: 'Entrevista', sub: 'Con fecha', pill: 'Entrevista', tone: 'info', empty: 'No hay entrevistas por hacer.' },
        { id: 'payment', label: 'Pago', sub: '', pill: 'Pago activado', tone: 'ok', empty: 'Nadie está en el paso del pago.' },
        { id: 'rejected', label: 'Rechazadas', sub: 'Se pueden reabrir', pill: 'Rechazada', tone: 'danger', empty: 'No hay solicitudes rechazadas.' }
    ],
    admissionStep: 'pending',
    admissionSearch: '',
    selectedAdmissionId: null,
    admissionsLoaded: false,

    admissionsOf(step) {
        const list = Array.isArray(this.admissionsList) ? this.admissionsList : [];
        const items = step === 'rejected' ? list.filter(e => e.status === 'rejected') : list.filter(e => e.status === step && !e.alreadyApproved);
        return items.sort((a, b) => String(b.updatedAt || b.createdAt || '').localeCompare(String(a.updatedAt || a.createdAt || '')));
    },

    applicantName(en) { return `${en.nombre || ''} ${en.apellidos || ''}`.trim(); },

    // Borradores sin formación elegida todavía: no se intenta nombrarla.
    admissionFormation(en, formations, full = false) {
        if (en.status === 'draft' && !FormationTypeService.ranksPreferences(en.tipoFormacion) && !en.formationId) return 'Sin formación elegida';
        return this.applicationLabel(en, formations, { full });
    },

    filteredAdmissions() {
        const query = Utils.normalizeString(this.admissionSearch.trim());
        const formations = FormationService.getFormations();
        const items = this.admissionsOf(this.admissionStep);
        if (!query) return items;
        return items.filter(en => [this.applicantName(en), en.email, en.dni, en.telefono, this.admissionFormation(en, formations)]
            .some(value => Utils.normalizeString(String(value || '')).includes(query)));
    },

    admissionDate(value, withYear = false) {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '—';
        return date.toLocaleDateString('es-ES', withYear ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' });
    },

    admissionPill(en) {
        if (en.status === 'payment') return en.paymentType ? `Pago recibido · ${en.paymentType === 'unico' ? 'único' : `${en.paymentMonths} meses`}` : 'Pago activado · esperando';
        return this.ADMISSION_STEPS.find(s => s.id === en.status)?.pill || en.status;
    },

    renderAdmissionsView() {
        return `
            <div class="view-container dnr-proposal sv-view sl ad">
                ${this.renderSectionTabs(StudentService.getStudents().length)}
                <ol class="ad-steps" id="ad-steps" aria-label="Pasos de la admisión">${this.renderAdmissionSteps()}</ol>
                <div class="ad-layout">
                    <section class="dnr-card ad-list-card" aria-labelledby="ad-list-title">
                        <div class="ad-list-head">
                            <div class="ad-row-between" id="ad-list-top">${this.renderAdmissionListTop()}</div>
                            <label class="ad-search">
                                <i class="fas fa-search" aria-hidden="true"></i>
                                <input type="search" id="ad-search" autocomplete="off" placeholder="Nombre, correo, DNI o formación" aria-label="Buscar solicitud"
                                    value="${Utils.sanitizeHTML(this.admissionSearch)}" ${UIEvents.attr("input", function(event,captured){StudentView.setAdmissionSearch(this.value);}, [])}>
                            </label>
                        </div>
                        <ul class="ad-list" id="ad-list">${this.renderAdmissionList()}</ul>
                    </section>
                    <section class="dnr-card ad-detail" id="ad-detail" aria-label="Solicitud elegida">${this.renderAdmissionDetail(this.selectedAdmission())}</section>
                </div>
            </div>
        `;
    },

    renderAdmissionSteps() {
        return this.ADMISSION_STEPS.map((step, i) => {
            const items = this.admissionsOf(step.id);
            const on = step.id === this.admissionStep;
            const sub = step.id === 'payment'
                ? `${items.filter(e => !e.paymentType).length} activado · ${items.filter(e => e.paymentType).length} recibido`
                : step.sub;
            return `<li><button type="button" class="ad-step is-${step.tone} ${on ? 'is-on' : ''}" data-step="${step.id}" aria-pressed="${on}" ${UIEvents.attr("click", function(event,captured){StudentView.setAdmissionStep(String(captured[0]));}, [step.id])}>
                <span class="ad-step-top"><span class="ad-step-label">${step.label}</span>${i < 3 ? '<span class="ad-step-arrow" aria-hidden="true">→</span>' : ''}</span>
                <span class="ad-step-count">${this.admissionsLoaded ? items.length : '…'}</span>
                <span class="ad-step-sub">${sub}</span>
            </button></li>`;
        }).join('');
    },

    renderAdmissionListTop() {
        const step = this.ADMISSION_STEPS.find(s => s.id === this.admissionStep);
        const count = this.admissionsLoaded ? this.filteredAdmissions().length : '…';
        return `<h2 class="sl-h2" id="ad-list-title">${step.label} · ${count}</h2>
            <span class="sl-muted">${step.id === 'draft' ? 'Aún no cuentan como solicitud' : 'La más reciente arriba'}</span>`;
    },

    renderAdmissionList() {
        if (!this.admissionsLoaded) return '<li class="ad-empty"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Cargando solicitudes…</li>';
        const items = this.filteredAdmissions();
        const step = this.ADMISSION_STEPS.find(s => s.id === this.admissionStep);
        if (!items.length) {
            return `<li class="ad-empty">${this.admissionSearch.trim() ? `Ninguna coincide con «${Utils.sanitizeHTML(this.admissionSearch.trim())}»` : step.empty}</li>`;
        }
        const formations = FormationService.getFormations();
        return items.map(en => {
            const name = this.applicantName(en);
            const on = en.id === this.selectedAdmissionId;
            const when = en.status === 'interview' && en.interviewDate
                ? `${this.admissionDate(en.interviewDate)}${en.interviewTime ? ` · ${Utils.sanitizeHTML(en.interviewTime)}` : ''}`
                : this.admissionDate(en.updatedAt || en.createdAt);
            return `<li><button type="button" class="ad-item ${on ? 'is-on' : ''}" data-id="${Utils.sanitizeHTML(en.id)}" ${on ? 'aria-current="true"' : ''} ${UIEvents.attr("click", function(event,captured){StudentView.selectAdmission(String(captured[0]));}, [en.id])}>
                <span class="ad-avatar" aria-hidden="true">${Utils.sanitizeHTML(name ? this.getInitials(en.nombre, en.apellidos) : '?')}</span>
                <span class="ad-item-text">
                    <span class="ad-item-top">${name ? `<strong>${Utils.sanitizeHTML(name)}</strong>` : '<em>Sin nombre todavía</em>'}<span class="ad-when">${when}</span></span>
                    <span class="ad-item-formation">${Utils.sanitizeHTML(this.admissionFormation(en, formations))}</span>
                    <span class="sl-pill ad-pill is-${step.tone}">${Utils.sanitizeHTML(this.admissionPill(en))}</span>
                </span>
            </button></li>`;
        }).join('');
    },

    selectedAdmission() {
        return (this.admissionsList || []).find(e => e.id === this.selectedAdmissionId) || null;
    },

    // Full application: the side panel of Admisiones and, outside that page, the modal.
    renderAdmissionDetail(en, { modal = false } = {}) {
        if (!en) {
            return `<div class="ad-detail-empty"><i class="far fa-folder-open" aria-hidden="true"></i><strong>Ninguna solicitud elegida</strong><span>Pulsa una de la lista para verla aquí entera.</span></div>`;
        }
        const s = value => Utils.sanitizeHTML(value || '');
        const formations = FormationService.getFormations();
        const name = this.applicantName(en);
        const fact = (label, value, wide = false) => `<div class="ad-fact ${wide ? 'is-wide' : ''}"><span class="ad-k">${label}</span><span class="ad-v">${value ? s(value) : '<span class="ad-none">—</span>'}</span></div>`;
        const birth = en.fechaNacimiento ? new Date(en.fechaNacimiento) : null;
        const age = birth && !Number.isNaN(birth.getTime()) ? Math.floor((Date.now() - birth.getTime()) / 31557600000) : null;
        const fileLink = (path, label) => path
            ? `<a class="ad-doc" href="/${s(path)}" target="_blank" rel="noopener"><span class="ad-doc-icon" aria-hidden="true"><i class="fas fa-file-arrow-down"></i></span>${label}</a>`
            : `<span class="ad-doc is-missing"><span class="ad-doc-icon" aria-hidden="true"><i class="fas fa-minus"></i></span>${label}: no aportado</span>`;
        const id = en.id, fullName = name || 'este borrador';
        const button = (label, cls, handler) => `<button type="button" class="sl-btn ${cls}" ${handler}>${label}</button>`;
        const actions = modal ? '' : en.status === 'draft'
            ? button('<i class="fas fa-arrow-up-right-from-square" aria-hidden="true"></i> Abrir su formulario', '', UIEvents.attr("click", function(event,captured){StudentView.openEnrollmentLink(String(captured[0]));}, [id]))
              + button('<i class="fas fa-trash-alt" aria-hidden="true"></i> Descartar borrador', 'is-danger', UIEvents.attr("click", function(event,captured){StudentView.rejectAdmission(String(captured[0]),String(captured[1]));}, [id, fullName]))
            : en.status === 'rejected'
            ? button('<i class="fas fa-undo" aria-hidden="true"></i> Reabrir', '', UIEvents.attr("click", function(event,captured){StudentView.reopenAdmission(String(captured[0]),String(captured[1]));}, [id, fullName]))
              + button('<i class="fas fa-check" aria-hidden="true"></i> Aprobar', 'is-primary', UIEvents.attr("click", function(event,captured){StudentView.approveAdmission(String(captured[0]),String(captured[1]));}, [id, fullName]))
            : button('<i class="fas fa-times" aria-hidden="true"></i> Rechazar', 'is-danger', UIEvents.attr("click", function(event,captured){StudentView.rejectAdmission(String(captured[0]),String(captured[1]));}, [id, fullName]))
              + button('<i class="fas fa-check" aria-hidden="true"></i> Aprobar y crear ficha', 'is-primary', UIEvents.attr("click", function(event,captured){StudentView.approveAdmission(String(captured[0]),String(captured[1]));}, [id, fullName]));
        const TRACK = ['Enviada', 'Revisada', 'Entrevista', 'Pago', 'Alumno'];
        const reached = { draft: -1, pending: 1, interview: 2, payment: 3, approved: 4 }[en.status];
        const track = en.status === 'rejected'
            ? `<p class="ad-rejected"><i class="fas fa-times-circle" aria-hidden="true"></i> Rechazada el ${this.admissionDate(en.updatedAt || en.createdAt, true)}. Se puede reabrir o aprobar igualmente.</p>`
            : `<ol class="ad-track" aria-label="Recorrido de la solicitud">${TRACK.map((label, i) => {
                const state = reached === undefined || reached < 0 ? '' : i < reached ? 'is-done' : i === reached ? 'is-now' : '';
                return `<li class="${state}" ${state === 'is-now' ? 'aria-current="step"' : ''}><span class="ad-dot" aria-hidden="true">${state === 'is-done' ? '✓' : i + 1}</span>${label}</li>`;
            }).join('')}</ol>`;
        const statusBlock = !en.studentId ? `
                <div class="sv-admission-locked ad-locked" role="status">
                    <strong>Paso: ${s({draft:'Borrador (incompleto)',pending:'Pendiente',interview:'Entrevista',entrevista_solicitada:'Entrevista solicitada',payment:'Pago activado',approved:'Aprobado',rejected:'Rechazado'}[en.status] || en.status)}</strong>
                    <span><i class="fas fa-lock" aria-hidden="true"></i> Aprueba primero la solicitud para programar la entrevista o activar el pago.</span>
                </div>` : `
                <label class="ad-field">Paso
                    <select id="admission-status-select" ${UIEvents.attr("change", function(event,captured){StudentView.onAdmissionStatusChange(this.value);}, [])}>
                        ${en.status === 'draft' ? `<option value="draft" selected disabled>Borrador (incompleto)</option>` : ''}
                        <option value="pending" ${en.status === 'pending' ? 'selected' : ''}>Pendiente de revisar</option>
                        <option value="interview" ${en.status === 'interview' ? 'selected' : ''}>Entrevista</option>
                        <option value="payment" ${en.status === 'payment' ? 'selected' : ''}>Activar pago</option>
                        <option value="approved" ${en.status === 'approved' ? 'selected' : ''}>Aprobada</option>
                        <option value="rejected" ${en.status === 'rejected' ? 'selected' : ''}>Rechazada</option>
                    </select>
                </label>
                <div id="interview-datetime-container" class="ad-interview" style="display: ${en.status === 'interview' ? 'grid' : 'none'};">
                    <label class="ad-field">Día de la entrevista<input type="date" id="admission-interview-date" value="${s(en.interviewDate)}"></label>
                    <label class="ad-field">Hora<input type="time" step="900" id="admission-interview-time" value="${s(en.interviewTime)}"></label>
                </div>
                <button type="button" class="sl-btn" ${UIEvents.attr("click", function(event,captured){StudentView.saveAdmissionStatus(String(captured[0]));}, [en.id])}><i class="fas fa-save" aria-hidden="true"></i> Guardar el paso</button>`;
        const payment = en.paymentType ? `
                <div class="ad-block">
                    <span class="ad-k">Pago · ${en.paymentType === 'unico' ? 'pago único' : `fraccionado en ${s(String(en.paymentMonths || ''))} meses`}</span>
                    <div class="ad-docs">${en.paymentType === 'unico'
                        ? fileLink(en.justificantePagoFile, 'Justificante de pago')
                        : fileLink(en.dniFrontalFile, 'DNI de quien financia · frontal') + fileLink(en.dniTraseraFile, 'DNI de quien financia · trasera') + fileLink(en.solicitudFraccionadoFile, 'Solicitud de financiación') + fileLink(en.declaracionIngresosFile, 'Nómina o justificante de ingresos')}</div>
                </div>` : '';
        const address = [en.direccion, [en.codigoPostal, en.localidad].filter(Boolean).join(' '), [en.provincia, en.pais].filter(Boolean).join(', ')].filter(Boolean).join(' · ');

        return `
            <div class="ad-detail-head">
                <div class="ad-who">
                    ${en.foto ? `<img class="ad-photo" src="/${s(en.foto)}" alt="">` : `<span class="ad-avatar is-big" aria-hidden="true">${s(name ? this.getInitials(en.nombre, en.apellidos) : '?')}</span>`}
                    <span class="ad-who-text"><h2>${name ? s(name) : '<em>Sin nombre todavía</em>'}</h2><span>${s(this.admissionFormation(en, formations, true))} · solicitud del ${this.admissionDate(en.createdAt || en.updatedAt, true)}</span></span>
                </div>
                ${actions ? `<div class="ad-actions">${actions}</div>` : ''}
            </div>
            <div class="ad-track-wrap">${track}</div>
            <div class="ad-body">
                <div class="ad-main">
                    <div class="ad-facts">
                        ${fact('Correo', en.email)}${fact('Teléfono', en.telefono)}
                        ${fact('DNI / NIE / pasaporte', en.dni)}${fact('Nacimiento', birth && !Number.isNaN(birth.getTime()) ? `${birth.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}${age !== null ? ` · ${age} años` : ''}` : '')}
                        ${fact('Sexo', en.sexo)}${fact('Cómo nos conoció', en.comoNosConociste)}
                        ${fact('Dirección', address, true)}
                    </div>
                    ${en.tutorNombre ? `
                    <div class="ad-block">
                        <span class="ad-k">Tutor legal (menor de edad)</span>
                        <div class="ad-facts">
                            ${fact('Nombre', en.tutorNombre)}${fact('Teléfono', en.tutorTelefono)}
                            ${fact('DNI', en.tutorDni)}${fact('Correo', en.tutorEmail)}
                            ${fact('Dirección', [en.tutorDireccion, [en.tutorCodigoPostal, en.tutorMunicipio].filter(Boolean).join(' '), [en.tutorProvincia, en.tutorPais].filter(Boolean).join(', ')].filter(Boolean).join(' · '), true)}
                        </div>
                    </div>` : ''}
                    <div class="ad-texts">
                        <div class="ad-block"><span class="ad-k">Presentación</span><p class="ad-text">${s(en.presentacionText) || '<span class="ad-none">No aportada</span>'}</p></div>
                        <div class="ad-block"><span class="ad-k">Estudios</span><p class="ad-text">${s(en.estudiosText) || '<span class="ad-none">No aportados</span>'}</p></div>
                    </div>
                    <div class="ad-block">
                        <span class="ad-k">Documentación adjunta</span>
                        <div class="ad-docs">${fileLink(en.dniFile, 'DNI · anverso')}${fileLink(en.dniBackFile, 'DNI · reverso')}${fileLink(en.presentacionFile, 'CV o carta')}${fileLink(en.estudiosFile, 'Acreditación de estudios')}</div>
                    </div>
                    ${payment}
                </div>
                <aside class="ad-side">
                    <span class="ad-k">Cambiar de paso</span>
                    ${statusBlock}
                    <p class="ad-foot">Al aprobarla se crea su ficha de alumno. Rechazarla la lleva a «Rechazadas», desde donde se puede reabrir.</p>
                </aside>
            </div>`;
    },

    setAdmissionStep(step) {
        if (!this.ADMISSION_STEPS.some(s => s.id === step)) return;
        this.admissionStep = step;
        this.selectedAdmissionId = null;
        this.refreshAdmissions();
    },

    // From the Alumnos page («Pendiente» box): straight to one step of Admisiones.
    openAdmissionStep(step) {
        this.admissionStep = step;
        this.selectedAdmissionId = null;
        this.switchSection('admissions');
    },

    setAdmissionSearch(value) {
        clearTimeout(this._admissionSearchTimer);
        this._admissionSearchTimer = setTimeout(() => {
            this.admissionSearch = value;
            this.refreshAdmissions({ keepSearch: true });
        }, 150);
    },

    selectAdmission(id) {
        this.selectedAdmissionId = id;
        document.querySelectorAll('#ad-list .ad-item').forEach(item => {
            const on = item.dataset.id === id;
            item.classList.toggle('is-on', on);
            if (on) item.setAttribute('aria-current', 'true'); else item.removeAttribute('aria-current');
        });
        const detail = document.getElementById('ad-detail');
        if (detail) SafeHTML.set(detail, this.renderAdmissionDetail(this.selectedAdmission()));
    },

    // Steps, list and the chosen application: the header and the search box stay.
    refreshAdmissions() {
        if (!document.getElementById('ad-list')) return;
        const visible = this.filteredAdmissions();
        if (!visible.some(e => e.id === this.selectedAdmissionId)) this.selectedAdmissionId = this.admissionsLoaded ? visible[0]?.id || null : null;
        SafeHTML.set(document.getElementById('ad-steps'), this.renderAdmissionSteps());
        SafeHTML.set(document.getElementById('ad-list-top'), this.renderAdmissionListTop());
        SafeHTML.set(document.getElementById('ad-list'), this.renderAdmissionList());
        SafeHTML.set(document.getElementById('ad-detail'), this.renderAdmissionDetail(this.selectedAdmission()));
    },

    // La interfaz refleja lo que permite el servidor: /api/enrollments exige
    // admin/coordinación/RRHH o el módulo Entrevistas; crear alumnos, gestionar Alumnado.
    canSeeAdmissions() {
        return ['admin','coordinator','rrhh'].includes(AuthManager.getCurrentUser()?.role) || AuthManager.hasModule('entrevistas');
    },

    canImportStudents() {
        return AuthManager.isAdmin() || AuthManager.isRRHH() || AuthManager.hasModule('alumnado', 'manage');
    },

    canAddStudents() {
        return AuthManager.canManageModule('alumnado', ['admin','coordinator']);
    },

    // Cambiar de curso lectivo (p. ej. ver el próximo) es cosa de gestión/admisiones.
    canBrowseYears() {
        return this.canSeeAdmissions() || this.canAddStudents();
    },

    // Contadores de las pestañas a partir de las solicitudes ya cargadas, para que un
    // repintado (p. ej. al cambiar de curso) no los oculte; updateBadgeCount los refresca.
    admissionCounts() {
        const list = Array.isArray(this.admissionsList) ? this.admissionsList : [];
        return {
            reviewable: list.filter(e => (e.status === 'pending' || e.status === 'interview' || e.status === 'payment') && !e.alreadyApproved).length,
            drafts: list.filter(e => e.status === 'draft' && !e.alreadyApproved).length
        };
    },

    // Cabecera común a las cuatro secciones: título, secciones como botones y las acciones de cada una.
    renderSectionTabs(registeredCount) {
        const admissions = this.canSeeAdmissions();
        const counts = this.admissionCounts();
        const section = this.currentSection === 'rejected' || this.currentSection === 'in_progress' ? 'admissions' : this.currentSection;
        const tab = (id, label, extra = '') => `<button type="button" class="sv-tab sl-sec ${section === id ? 'active' : ''}" data-section="${id}" aria-pressed="${section === id}" ${UIEvents.attr("click", function(event,captured){StudentView.switchSection(String(captured[0]));}, [id])}>${label}${extra}</button>`;
        let actions = '';
        if (this.currentSection === 'registered') {
            actions = `
                    ${this.canImportStudents() ? `<button type="button" class="sl-btn" ${UIEvents.attr("click", function(event,captured){StudentView.openImportModal();}, [])}>
                        <i class="fas fa-file-excel" aria-hidden="true"></i> Importar Excel
                    </button>` : ''}
                    ${this.canAddStudents() ? `<button type="button" class="sl-btn is-primary" ${UIEvents.attr("click", function(event,captured){StudentView.openAddModal();}, [])}>
                        <i class="fas fa-plus" aria-hidden="true"></i> Añadir alumno
                    </button>` : ''}`;
        } else if (this.currentSection === 'formations') {
            actions = `
                <button type="button" class="sl-btn is-primary" ${UIEvents.attr("click", function(event,captured){StudentView.showAddFormationModal();}, [])}>
                    <i class="fas fa-plus" aria-hidden="true"></i> Añadir formación
                </button>`;
        }

        return `
            <div class="sl-head">
                <div class="sl-head-text">
                    <span class="sl-mono" id="sl-eyebrow">Alumnado y CRM · Curso ${this.currentYearStart} / ${this.currentYearStart + 1}</span>
                    <h1 class="sl-title">Alumnado</h1>
                </div>
                <div class="sl-head-actions">
                    ${admissions ? `
                    <nav class="sv-tabs sl-secs" aria-label="Secciones">
                        ${tab('registered', `Alumnos · <span id="sl-registered-count">${registeredCount}</span>`)}
                        ${tab('admissions', 'Admisiones', ` <span id="admissions-count-badge" class="sv-tab-count sl-badge" title="Solicitudes por revisar" style="display: ${counts.reviewable > 0 ? 'inline-block' : 'none'};">${counts.reviewable}</span>`)}
                        ${tab('formations', '<i class="fas fa-gear" aria-hidden="true"></i> Formaciones admisión')}
                    </nav>
                    ${actions.trim() ? '<span class="sl-sep" aria-hidden="true"></span>' : ''}` : ''}
                    <span class="sl-actions-slot">${actions}</span>
                </div>
            </div>
        `;
    },

    switchSection(section, subSection = null) {
        if (section === 'rejected') {
            this.currentSection = 'in_progress';
            this.inProgressSubSection = 'rejected';
        } else {
            this.currentSection = section;
            if (subSection) {
                this.inProgressSubSection = subSection;
            } else if (section === 'in_progress' && !this.inProgressSubSection) {
                this.inProgressSubSection = 'drafts';
            }
        }
        this.currentPage = 1;
        this.selectedStudents.clear();
        this.pendingFilter = null;
        this.repaintSection();
        this.init();
    },

    // Cambiar de sección: la cabecera se queda (solo cambian el botón activo y las acciones)
    // y se sustituye lo de debajo, sin repintar la página entera.
    repaintSection() {
        const container = document.getElementById('content-area');
        if (!container) return;
        const root = container.querySelector('.sv-view'), head = root?.querySelector(':scope > .sl-head');
        const fresh = document.createElement('div');
        SafeHTML.set(fresh, this.render());
        const freshRoot = fresh.querySelector('.sv-view'), freshHead = freshRoot?.querySelector(':scope > .sl-head');
        if (!root || !head || !freshRoot || !freshHead) { SafeHTML.set(container, this.render()); return; }
        root.className = freshRoot.className;
        head.querySelectorAll('.sl-sec').forEach(button => {
            const next = freshHead.querySelector(`.sl-sec[data-section="${button.dataset.section}"]`);
            if (!next) return;
            button.classList.toggle('active', next.classList.contains('active'));
            button.setAttribute('aria-pressed', next.getAttribute('aria-pressed'));
        });
        for (const selector of ['.sl-actions-slot', '#sl-eyebrow']) {
            const now = head.querySelector(selector), next = freshHead.querySelector(selector);
            if (now && next) now.replaceWith(next);
        }
        const sep = head.querySelector('.sl-sep'), freshSep = freshHead.querySelector('.sl-sep');
        if (sep && !freshSep) sep.remove();
        else if (!sep && freshSep) head.querySelector('.sl-secs')?.after(freshSep);
        while (head.nextSibling) head.nextSibling.remove();
        root.append(...[...freshRoot.childNodes].filter(node => node !== freshHead));
    },

    async updateBadgeCount() {
        if (!this.canSeeAdmissions()) return; // sin permiso el servidor respondería 403
        const token = AuthManager.getCsrfToken();
        try {
            const response = await fetch('/api/enrollments', {
                headers: { 'X-SICAF-CSRF': token }
            });
            if (response.ok) {
                const res = await response.json();
                if (res.success && res.enrollments) {
                    this.admissionsList = res.enrollments;
                    this.admissionsLoaded = true;
                    // Las cifras y la caja «Pendiente» cuentan las solicitudes: se actualizan al llegar.
                    if (this.currentSection === 'registered') this.refreshSide();
                    const reviewable = res.enrollments.filter(e => (e.status === 'pending' || e.status === 'interview' || e.status === 'payment') && !e.alreadyApproved);
                    const badge = document.getElementById('admissions-count-badge');
                    if (badge) {
                        badge.textContent = reviewable.length;
                        badge.style.display = reviewable.length > 0 ? 'inline-block' : 'none';
                    }

                    if (this.currentSection === 'admissions') this.refreshAdmissions();
                }
            }
        } catch (e) {
            console.error('Error updating admissions badge:', e);
        }
    },

    // Abre el enlace público de continuación (#inscribete/<token>) en una
    // pestaña nueva — para que el staff pueda ver/retomar el formulario tal
    // cual lo dejó el aspirante, sin salir de Alumnado.
    async openEnrollmentLink(id) {
        // The token is the applicant's credential: listings no longer carry it, so it is
        // requested here (admin, coordination or 'entrevistas' at manage level).
        const tab = window.open('', '_blank'); // opened before the await so the browser allows it
        try {
            const response = await fetch(`/api/enrollments/${encodeURIComponent(id)}/link`, { method: 'POST', headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() } });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.error || 'No tienes permiso para abrir el enlace de continuación');
            tab.location = `${window.location.origin}/inscribete/${encodeURIComponent(result.token)}`;
        } catch (error) {
            tab?.close();
            Utils.showToast(error.message, 'error');
        }
    },

    async reopenAdmission(id, name) {
        SafeHTML.fire({
            title: '¿Reabrir solicitud de matrícula?',
            html: `La solicitud de <b>${Utils.sanitizeHTML(name)}</b> pasará de nuevo a estado <b>Pendiente</b> de revisión.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#d97706',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, reabrir',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = AuthManager.getCsrfToken();
                    const response = await fetch(`/api/enrollments/status/${id}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SICAF-CSRF': token
                        },
                        body: JSON.stringify({ status: 'pending' })
                    });
                    const res = await response.json();
                    if (res.success) {
                        Utils.showToast('Solicitud reactivada a Pendiente', 'success');
                        this.updateBadgeCount();
                    } else {
                        throw new Error(res.error || 'Server error');
                    }
                } catch (err) {
                    SafeHTML.fire('Error', err.message || 'No se pudo reabrir la solicitud.', 'error');
                }
            }
        });
    },

    async viewAdmissionDetails(id) {
        let en = this.admissionsList.find(item => item.id === id);
        
        // Si no está en memoria (sesión expirada o recarga), intentar desde la API
        if (!en) {
            try {
                const jwtTok = AuthManager.getCsrfToken();
                const resp = await fetch('/api/enrollments', {
                    headers: { 'X-SICAF-CSRF': jwtTok }
                });
                if (resp.ok) {
                    const res = await resp.json();
                    if (res.success && res.enrollments) {
                        this.admissionsList = res.enrollments;
                        en = this.admissionsList.find(item => item.id === id);
                    }
                }
            } catch(e) {
                console.error('Error fetching enrollment details:', e);
            }
        }
        
        if (!en) {
            Utils.showToast('No se pudo cargar el detalle. Recarga la página e inicia sesión de nuevo.', 'error');
            return;
        }

        // On Admisiones the application opens in the side panel; elsewhere in a modal.
        if (document.getElementById('ad-detail') && this.currentSection === 'admissions') {
            if (this.admissionStep !== (en.status === 'rejected' ? 'rejected' : en.status) && this.ADMISSION_STEPS.some(step => step.id === en.status)) {
                this.admissionStep = en.status;
                this.refreshAdmissions();
            }
            this.selectAdmission(en.id);
            return;
        }
        const html = `<div class="dnr-proposal sl ad ad-modal">${this.renderAdmissionDetail(en, { modal: true })}</div>`;

        // Un borrador ('draft') todavía no es una solicitud completa — no tiene
        // sentido "Aprobar Matrícula" con datos a medio rellenar (crearía un
        // Student con campos vacíos), así que aquí solo se ofrece descartarlo.
        const isDraft = en.status === 'draft';
        ModalManager.show('Detalle de Inscripción', html, isDraft ? [
            {
                text: 'Descartar Borrador',
                class: 'btn-secondary',
                action: () => {
                    ModalManager.close();
                    this.rejectAdmission(en.id, `${en.nombre} ${en.apellidos}`.trim() || 'este borrador');
                }
            }
        ] : [
            {
                text: 'Rechazar',
                class: 'btn-secondary',
                action: () => {
                    ModalManager.close();
                    this.rejectAdmission(en.id, `${en.nombre} ${en.apellidos}`);
                }
            },
            {
                text: 'Aprobar Matrícula',
                class: 'btn-success',
                action: () => {
                    ModalManager.close();
                    this.approveAdmission(en.id, `${en.nombre} ${en.apellidos}`);
                }
            }
        ]);
    },

    async approveAdmission(id, name) {
        SafeHTML.fire({
            title: `¿Aprobar solicitud de matrícula?`,
            html: `Se procederá a registrar al alumno <b>${Utils.sanitizeHTML(name)}</b> en el centro. Su cuenta de acceso al portal se creará automáticamente (con email de bienvenida) cuando se confirme su pago en Prematrícula, no antes.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, aprobar matrícula',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                // Solicitud "para revisar": confirmar alumno nuevo o vincularla antes.
                const enrollment = (this.admissionsList || []).find(e => e.id === id) || {};
                const link = await this.resolveEnrollmentLink(id, {
                    requiresLinkReview: enrollment.requiresLinkReview, dni: enrollment.dni, email: enrollment.email,
                    name: `${enrollment.nombre || ''} ${enrollment.apellidos || ''}`.trim() || name
                });
                if (!link) return;
                try {
                    SafeHTML.fire({
                        title: 'Procesando...',
                        html: 'Registrando alumno y sincronizando datos.',
                        allowOutsideClick: false,
                        didOpen: () => {
                            Swal.showLoading();
                        }
                    });

                    const token = AuthManager.getCsrfToken();
                    const response = await fetch(`/api/enrollments/approve/${id}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': token },
                        body: JSON.stringify(link)
                    });

                    if (!response.ok) throw new Error('Database insertion failed');
                    const res = await response.json();

                    if (res.success) {
                        SafeHTML.fire({
                            icon: 'success',
                            title: 'Matrícula Aprobada',
                            html: `El estudiante se ha registrado correctamente en el centro.<br>Su cuenta de acceso se creará cuando se confirme el pago en Prematrícula.`
                        });

                        // Hydrate student services / storage and reload view
                        if (typeof FileStorage !== 'undefined') {
                            await FileStorage.loadAllFromServer(); // Sync student list
                        }
                        this.updateBadgeCount(); // Reload list and badges
                    } else {
                        throw new Error(res.error || 'Server error');
                    }
                } catch (err) {
                    SafeHTML.fire('Error', err.message || 'No se pudo completar la aprobación.', 'error');
                }
            }
        });
    },

    async rejectAdmission(id, name) {
        SafeHTML.fire({
            title: `¿Rechazar solicitud de matrícula?`,
            html: `La solicitud de <b>${Utils.sanitizeHTML(name)}</b> se moverá a la pestaña de <b>Rechazados</b> (dentro de En Proceso), desde donde podrá consultarse o reabrirse.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--danger-color)',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, rechazar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = AuthManager.getCsrfToken();
                    const response = await fetch(`/api/enrollments/reject/${id}`, {
                        method: 'POST',
                        headers: { 'X-SICAF-CSRF': token }
                    });

                    if (!response.ok) throw new Error('Database action failed');
                    const res = await response.json();

                    if (res.success) {
                        SafeHTML.fire('Matrícula Rechazada', 'La solicitud ha sido descartada.', 'success');
                        this.updateBadgeCount();
                    } else {
                        throw new Error(res.error || 'Server error');
                    }
                } catch (err) {
                    SafeHTML.fire('Error', err.message || 'No se pudo rechazar la solicitud.', 'error');
                }
            }
        });
    },

    onAdmissionStatusChange(value) {
        const container = document.getElementById('interview-datetime-container');
        if (container) {
            container.style.display = value === 'interview' ? (container.classList.contains('ad-interview') ? 'grid' : 'flex') : 'none';
        }
    },

    async saveAdmissionStatus(id) {
        if (!AuthManager.requireModuleManage("entrevistas",["admin","coordinator"])) return;
        const application = (this.admissionsList || []).find(e => e.id === id);
        if (application && !application.studentId) {
            SafeHTML.fire('Aprueba primero la solicitud', 'Hasta aprobarla no se pueden programar entrevistas, abrir el pago ni cambiar su estado.', 'info');
            return;
        }
        try {
            const statusEl = document.getElementById('admission-status-select');
            if (!statusEl) return;
            
            const status = statusEl.value;
            const interviewDate = document.getElementById('admission-interview-date')?.value || '';
            const interviewTime = document.getElementById('admission-interview-time')?.value || '';

            SafeHTML.fire({
                title: 'Guardando...',
                html: 'Actualizando el estado de la solicitud.',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const token = AuthManager.getCsrfToken();
            const response = await fetch(`/api/enrollments/status/${id}`, {
                method: 'POST',
                headers: { 
                    'X-SICAF-CSRF': token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status, interviewDate, interviewTime })
            });

            if (!response.ok) throw new Error('No se pudo guardar el estado');
            const res = await response.json();

            if (res.success) {
                SafeHTML.fire('Estado Guardado', 'El estado de la matrícula ha sido actualizado.', 'success');
                ModalManager.close();
                StudentView.updateBadgeCount();
            } else {
                throw new Error(res.error || 'Error del servidor');
            }
        } catch (err) {
            SafeHTML.fire('Error', err.message || 'No se pudo guardar el estado.', 'error');
        }
    },

    showAddFormationModal() {
        const content = `
            <div class="dnr-proposal students-form-dnr">
            <form id="admission-formation-form" ${UIEvents.attr("submit", function(event,captured){StudentView.handleFormationSubmit(event);}, [])}>
                <input type="hidden" name="enrollmentOpen" value="true">

                <div class="form-group">
                    <label class="form-label">Tipo de Formación *</label>
                    <select class="form-input" name="type" required ${UIEvents.attr("change", function(event,captured){StudentView.fillAdmissionYears(this);}, [])}>
                        <option value="">Seleccionar tipo...</option>
                        ${FormationTypeService.getTypes({includeInactive: false}).map(t => `<option value="${Utils.sanitizeHTML(t.id)}">${Utils.sanitizeHTML(t.name)}</option>`).join('')}
                    </select>
                </div>

                <div class="form-group">
                    <label class="form-label">Nombre *</label>
                    <input type="text" class="form-input" name="name" required placeholder="Nombre de la formación">
                </div>

                <div class="form-group">
                    <label class="form-label">Nombre en el formulario de inscripción</label>
                    <input type="text" class="form-input" name="commercialName" maxlength="300" placeholder="Si se deja vacío, se usa el nombre">
                </div>

                <div class="form-group">
                    <label class="form-label">Año Académico</label>
                    <select class="form-input" name="academicYear">
                        <option value="">Elige primero el tipo</option>
                    </select>
                </div>

                <div class="flex gap-1" style="justify-content: flex-end; margin-top: 1.5rem;">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary">
                        <i class="fas fa-save"></i> Crear Formación
                    </button>
                </div>
            </form>
            </div>
        `;
        Utils.openModal('Añadir Formación para Admisión', content);
    },

    handleFormationSubmit(event) {
        event.preventDefault();
        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData);
        
        // Force boolean type for enrollmentOpen
        data.enrollmentOpen = true;

        DataManager.createFormation(data);
        Utils.closeModal();
        Utils.showToast('Formación creada y abierta a inscripción', 'success');
        
        // Only the table and the public list change (Admisiones -> Formaciones)
        if (document.getElementById('fm-card')) this.refreshFormations(); else App.render();
    },

    deleteFormation(id, name) {
        Utils.showConfirm(
            `¿Estás seguro de que quieres eliminar la formación "${Utils.sanitizeHTML(name)}"?<br><br><small style="color:red">Nota: Esto la eliminará de todo el sistema, no solo de admisiones.</small>`,
            () => {
                DataManager.deleteFormation(id);
                Utils.showToast('Formación eliminada', 'success');
                if (document.getElementById('fm-card')) this.refreshFormations(); else App.render();
            }
        );
    },

    // Formaciones abiertas a admisión (diseño «P»): una tabla con filtro de tipo y, al lado, lo que
    // puede elegirse hoy en el formulario público. Solo el primer año de cada tipo se puede abrir.
    formationTypeFilter: 'all',

    isFirstYear(f) {
        const first = FormationTypeService.years(f.type)[0]?.id;
        return !f.academicYear || !first || String(f.academicYear) === String(first);
    },

    renderFormationsView() {
        return `
            <div class="view-container dnr-proposal sv-view sl fm-page">
                ${this.renderSectionTabs(StudentService.getStudents().length)}
                <div class="fm-layout">
                    <section class="dnr-card fm-card" id="fm-card" aria-labelledby="fm-title">${this.renderFormationsCard()}</section>
                    <aside class="fm-aside" id="fm-aside">${this.renderFormationsAside()}</aside>
                </div>
            </div>
        `;
    },

    renderFormationsCard() {
        const formations = FormationService.getFormations();
        const types = FormationTypeService.getTypes();
        const typeIndex = id => { const i = types.findIndex(t => t.id === id); return i < 0 ? types.length : i; };
        const firstYear = formations.filter(f => this.isFirstYear(f));
        const open = firstYear.filter(f => f.enrollmentOpen).length;
        const shownTypes = types.filter(t => firstYear.some(f => f.type === t.id));
        if (this.formationTypeFilter !== 'all' && !shownTypes.some(t => t.id === this.formationTypeFilter)) this.formationTypeFilter = 'all';
        // Solo el primer año: a 2.º y 3.º se llega promocionando, nunca por el formulario.
        const rows = firstYear.filter(f => this.formationTypeFilter === 'all' || f.type === this.formationTypeFilter)
            .sort((a, b) => typeIndex(a.type) - typeIndex(b.type) || String(a.name).localeCompare(String(b.name), 'es'));
        const typeButton = (id, label) => {
            const on = this.formationTypeFilter === id;
            return `<button type="button" class="sl-type ${on ? 'is-on' : ''}" aria-pressed="${on}" ${UIEvents.attr("click", function(event,captured){StudentView.setFormationTypeFilter(String(captured[0]));}, [id])}>${Utils.sanitizeHTML(label)}</button>`;
        };
        return `
            <div class="fm-head">
                <h2 class="sl-h2" id="fm-title">Formaciones · ${open} ${open === 1 ? 'abierta' : 'abiertas'} de ${firstYear.length}</h2>
                ${shownTypes.length > 1 ? `<div class="sl-types" role="group" aria-label="Tipo de formación">${typeButton('all', 'Todas')}${shownTypes.map(t => typeButton(t.id, t.pluralName)).join('')}</div>` : ''}
            </div>
            <div class="sl-scroll">
                <table class="sl-table fm-table">
                    <thead><tr>
                        <th scope="col">Formación</th>
                        <th scope="col" class="fm-col-type">Tipo</th>
                        <th scope="col" class="fm-col-open">En el formulario público</th>
                        <th scope="col" class="fm-col-del"><span class="dv-sr-only">Acciones</span></th>
                    </tr></thead>
                    <tbody>
                        ${rows.length ? rows.map(f => {
                            return `<tr>
                                <td><strong>${Utils.sanitizeHTML(f.name)}</strong></td>
                                <td class="fm-col-type">${Utils.sanitizeHTML(FormationTypeService.getType(f.type)?.name || FormationService.getFormationTypeLabel(f.type) || '—')}</td>
                                <td class="fm-col-open">
                                    <label class="fm-switch">
                                        <input type="checkbox" role="switch" ${f.enrollmentOpen ? 'checked' : ''} aria-label="${Utils.sanitizeHTML(f.name)} en el formulario público" ${UIEvents.attr("change", function(event,captured){StudentView.toggleFormationEnrollment(String(captured[0]),this.checked);}, [f.id])}>
                                        <span class="fm-track" aria-hidden="true"></span><span class="fm-state">${f.enrollmentOpen ? 'Abierta' : 'Cerrada'}</span>
                                    </label></td>
                                <td class="fm-col-del"><button type="button" class="sl-mini-btn fm-rename" aria-label="Cambiar el nombre de ${Utils.sanitizeHTML(f.name)} en el formulario" title="Nombre en el formulario de inscripción" ${UIEvents.attr("click", function(event,captured){StudentView.editFormationPublicName(String(captured[0]));}, [f.id])}><i class="fas fa-pen"></i></button><button type="button" class="sl-mini-btn is-danger" aria-label="Borrar ${Utils.sanitizeHTML(f.name)}" title="Borrar formación" ${UIEvents.attr("click", function(event,captured){StudentView.deleteFormation(String(captured[0]),String(captured[1]));}, [f.id, f.name])}><i class="fas fa-trash-alt"></i></button></td>
                            </tr>`;
                        }).join('') : '<tr><td colspan="4"><div class="dnr-empty">No hay formaciones de este tipo.</div></td></tr>'}
                    </tbody>
                </table>
            </div>`;
    },

    renderFormationsAside() {
        const open = FormationService.getFormations().filter(f => this.isFirstYear(f) && f.enrollmentOpen)
            .sort((a, b) => this.formationPublicName(a).localeCompare(this.formationPublicName(b), 'es'));
        return `
            <section class="dnr-card sl-side" aria-labelledby="fm-public-title">
                <h2 class="sl-h2" id="fm-public-title">Así lo ve quien se inscribe</h2>
                <span class="sl-muted">Estas son las formaciones que se pueden elegir hoy en el formulario de inscripción.</span>
                ${open.length ? `<ul class="fm-public">${open.map(f => `<li><span class="fm-radio" aria-hidden="true"></span>${Utils.sanitizeHTML(this.formationPublicName(f))}</li>`).join('')}</ul>`
                    : '<p class="fm-warn">Ninguna abierta: el formulario no deja elegir formación.</p>'}
            </section>
            <section class="dnr-card sl-side fm-note">
                <strong>Cómo funciona</strong>
                <span>Aquí solo están los primeros años: a 2.º y 3.º no se entra por el formulario, se llega promocionando o se da de alta directamente.</span>
            </section>`;
    },

    setFormationTypeFilter(type) {
        this.formationTypeFilter = type;
        this.refreshFormations();
    },

    refreshFormations() {
        const card = document.getElementById('fm-card'), aside = document.getElementById('fm-aside');
        if (card) SafeHTML.set(card, this.renderFormationsCard());
        if (aside) SafeHTML.set(aside, this.renderFormationsAside());
    },

    formationPublicName(f) {
        return String(f.commercialName && f.commercialName !== 'null' ? f.commercialName : f.name);
    },

    editFormationPublicName(formationId) {
        const f = FormationService.getFormations().find(x => x.id === formationId);
        if (!f) return;
        const current = f.commercialName && f.commercialName !== 'null' ? f.commercialName : '';
        const content = `
            <div class="dnr-proposal students-form-dnr">
            <form ${UIEvents.attr("submit", function(event,captured){event.preventDefault();StudentView.setFormationPublicName(String(captured[0]),this.elements.commercialName.value);Utils.closeModal();}, [f.id])}>
                <div class="form-group">
                    <label class="form-label" for="fm-public-name-input">Nombre en el formulario de inscripción</label>
                    <input type="text" class="form-input" id="fm-public-name-input" name="commercialName" maxlength="300" value="${Utils.sanitizeHTML(current)}" placeholder="${Utils.sanitizeHTML(f.name)}">
                    <small class="sl-muted">Si se deja vacío, sale «${Utils.sanitizeHTML(f.name)}».</small>
                </div>
                <div class="flex gap-1" style="justify-content: flex-end; margin-top: 1.5rem;">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-save"></i> Guardar</button>
                </div>
            </form>
            </div>
        `;
        Utils.openModal(`Nombre en el formulario · ${f.name}`, content);
        setTimeout(() => document.getElementById('fm-public-name-input')?.focus(), 50);
    },

    // Nombre con el que se anuncia (commercialName): es el que ve quien se inscribe. Vacío = el nombre interno.
    setFormationPublicName(formationId, value) {
        const name = String(value || '').trim();
        try {
            DataManager.updateFormation(formationId, { commercialName: name || null });
            Utils.showToast(name ? 'Nombre del formulario guardado' : 'El formulario usará el nombre de la formación', 'success');
        } catch (error) {
            console.error('Error saving public formation name:', error);
            Utils.showToast('No se pudo guardar el nombre', 'error');
        }
        const aside = document.getElementById('fm-aside');
        if (aside) SafeHTML.set(aside, this.renderFormationsAside());
    },

    async toggleFormationEnrollment(formationId, isChecked) {
        try {
            // Update using the central DataManager
            DataManager.updateFormation(formationId, { enrollmentOpen: isChecked });
            Utils.showToast(`Formación ${isChecked ? 'abierta' : 'cerrada'} para matrícula correctamente`, 'success');
        } catch (error) {
            console.error('Error toggling formation enrollment:', error);
            Utils.showToast(`Error al actualizar la formación`, 'error');
        }
        // Count, switch label and the public list follow the stored value (also after a failure).
        this.refreshFormations();
    }
};

window.StudentView = StudentView;
